// ================================================================================
// THEME-CUSTOM.JS - Custom JavaScript Components
// ================================================================================
// All custom business logic, overrides, and enhancements
// Loaded after theme.js to extend base functionality
//
// TABLE OF CONTENTS:
// ------------------
// 1. TYPE WRITER TEXT           (Line 3)    - Animated text typing effect
// 2. FREE SHIPPING BAR          (Line 89)   - Dynamic cart threshold messaging
// 3. ANNOUNCEMENT COUNTDOWN     (Line 200)  - Countdown timer for announcements
// 4. BUNDLE GAMIFICATION        (Line 2159) - Product bundle progress tracking
// 5. SECONDARY MEDIA OVERRIDE   (Line 2283) - Flickity A11Y for product images
// 6. PRODUCT CARD INFO          (Line 3199) - Dynamic product card information
//
// BC_ TAGGED SECTIONS:
// --------------------
// - BC_BUNDLE_POLISH     (Line 2161) - Bundle console logging improvements
// - BC_FLICKITY_A11Y     (Line 2270) - Documentation block for accessibility
// - BC_FLICKITY_A11Y     (Line 2324) - SecondaryMedia carousel labels
//
// ================================================================================

window.theme = window.theme || {};

// ================================================================================
// 1. TYPE WRITER TEXT - Animated text typing effect
// ================================================================================
theme.TypeWriterText = (function() {
  class TypeWriterText extends HTMLElement {
    constructor() {
      super();
      this.texts = this.dataset.texts.split(',').map(text => text.trim());
      this.delay = parseFloat(this.dataset.delay) * 1000;
      this.typingSpeed = parseFloat(this.dataset.typingSpeed);  // This is in milliseconds
      this.humanVariance = parseFloat(this.dataset.humanVariance) / 100;
      this.currentIndex = 0;

      Motion.inView(this, this.init.bind(this));
    }

    get cursor() {
      return this._cursor;
    }

    set cursor(el) {
      this._cursor = el;
    }

    async init() {
      this.insertCursor();
      await this.startRotation();
    }

    getRandomDelay() {
      const variance = this.typingSpeed * this.humanVariance;
      return this.typingSpeed + (Math.random() * variance * 2 - variance);
    }

    async typeText(text) {
      let currentText = '';
      for (const char of text) {
        currentText += char;
        this.textContent = currentText;
        // Remove the Motion.animate call since it's forcing a fixed duration
        // Just update the width immediately
        this.style.width = `${this.scrollWidth}px`;
        // Use the configured typing speed for the delay
        await new Promise(resolve => setTimeout(resolve, this.getRandomDelay()));
      }
    }

    async startRotation() {
      while (true) {
        const text = this.texts[this.currentIndex];

        // Clear previous text
        this.textContent = '';
        await Motion.animate(this, { width: 0 }, { duration: 0.1 }).finished;

        // Type out new text
        await this.typeText(text);

        // Show cursor and wait
        this.cursor.classList.add('blink');
        await new Promise(resolve => setTimeout(resolve, this.delay));

        // Hide cursor and prepare for next word
        this.cursor.classList.remove('blink');

        // Move to next text
        this.currentIndex = (this.currentIndex + 1) % this.texts.length;
      }
    }

    insertCursor() {
      if (this.cursor) return;

      this.cursor = document.createElement('span');
      this.cursor.className = 'typed-cursor';
      this.cursor.setAttribute('aria-hidden', true);
      this.cursor.innerHTML = '|';
      this.parentElement.insertBefore(this.cursor, this.nextSibling);
    }
  }

  return TypeWriterText;
})();

// Register custom element
customElements.define('type-writer-text', theme.TypeWriterText);

// ================================================================================
// 2. FREE SHIPPING BAR - Dynamic cart threshold messaging
// ================================================================================
theme.FreeShippingAnnouncement = (function() {
  class FreeShippingAnnouncement extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.closest('.shopify-section').id.replace('shopify-section-', '');
      this.minimumAmount = parseFloat(this.dataset.minimumAmount);
      this.originalText = this.dataset.text;
      this.isVisible = true;
      this.lastCartTotal = parseFloat(this.dataset.cartTotal) / 100;
      this.bindEvents();

      // Initialize the message immediately
      this.updateShippingMessage(this.lastCartTotal);
    }

    bindEvents() {
      // Listen for cart updates
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          if (event.cart) {
            this.onCartUpdate(event);
          }
        }
      );

      // Listen for the native cart:updated event
      document.addEventListener('cart:updated', (event) => {
        if (event.detail && event.detail.cart) {
          this.onCartUpdate({ cart: event.detail.cart });
        }
      });

      // Listen for slide visibility changes
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) {
            // Force update when becoming visible
            this.updateShippingMessage(this.lastCartTotal);
          }
        });
      }, { threshold: 0.1 });

      this.observer.observe(this);

      // Listen for section updates
      document.addEventListener('cart:bundled-sections', (event) => {
        if (event.detail && event.detail.sections) {
          event.detail.sections.push(this.sectionId);
        }
      });
    }

    onCartUpdate(event) {
      if (!event.cart) return;

      const cartTotal = event.cart.total_price / 100;
      this.lastCartTotal = cartTotal;

      // Update data attribute for other components
      this.dataset.cartTotal = event.cart.total_price;

      // Always update the message when cart changes
      this.updateShippingMessage(cartTotal);
    }

    updateShippingMessage(cartTotal) {
      const remainingAmount = Math.max(0, this.minimumAmount - cartTotal);
      const shippingMessage = this.querySelector('.shipping-message');

      if (!shippingMessage) return;

      // Update the message content
      if (remainingAmount <= 0) {
        shippingMessage.innerHTML = this.dataset.congratulationsText;
      } else {
        const formattedAmount = `$${remainingAmount.toFixed(2)}`;
        const messageHTML = this.originalText.replace(
          '[remaining_amount]',
          `<span class="remaining-amount font-bold">${formattedAmount}</span>`
        );
        shippingMessage.innerHTML = messageHTML;
      }

      // Dispatch a custom event for other components
      this.dispatchEvent(new CustomEvent('shipping-message-updated', {
        detail: {
          cartTotal,
          remainingAmount,
          minimumAmount: this.minimumAmount
        }
      }));
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  return FreeShippingAnnouncement;
})();

// Register custom element
customElements.define('free-shipping-announcement', theme.FreeShippingAnnouncement);

// ================================================================================
// 3. ANNOUNCEMENT COUNTDOWN - Countdown timer for announcements
// ================================================================================
theme.AnnouncementCountdownTimer = (function() {
  class AnnouncementCountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.activeMessage = this.getAttribute('data-active-message');
      this.completeMessage = this.getAttribute('data-complete-message');
      this.messageElement = this.parentElement.querySelector('[data-timer-message]');
      this.expirationDate = new Date(this.getAttribute('data-expires')).getTime();
      this.timerInterval = null;

      Motion.inView(this, this.init.bind(this));
    }

    init() {
      this.startCountdown();
    }

    startCountdown() {
      this.updateCountdown();
      this.timerInterval = setInterval(() => {
        this.updateCountdown();
      }, 1000);
    }

    updateCountdown() {
      const now = new Date().getTime();
      const distance = this.expirationDate - now;

      if (distance < 0) {
        this.complete();
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      this.updateDisplay(days, hours, minutes, seconds);
    }

    updateDisplay(days, hours, minutes, seconds) {
      const items = this.querySelectorAll('.countdown__item p');
      if (items.length === 4) {
        items[0].textContent = days;
        items[1].textContent = hours;
        items[2].textContent = minutes;
        items[3].textContent = seconds;
      }
    }

    complete() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      // Show complete message if available
      if (this.completeMessage && this.messageElement) {
        this.messageElement.innerHTML = this.completeMessage;
      }

      // Hide the timer digits
      this.style.display = 'none';
    }

    disconnectedCallback() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }
    }
  }

  return AnnouncementCountdownTimer;
})();

// Register with a unique name
customElements.define('announcement-countdown-timer', theme.AnnouncementCountdownTimer);

// Fix for re-animation "flash" on section re-renders
(function() {
  // Use a Set to track elements that have already run their initial animation,
  // using their text content as a unique identifier for the session.
  const animatedElementKeys = new Set();

  const processNode = (el) => {
    // Generate a key. If the element has no text, it can't be tracked this way.
    const key = el.textContent?.trim();
    if (!key) return;

    if (animatedElementKeys.has(key)) {
      // This element's content has been animated before.
      // Prevent the "flash" by immediately setting its final animation state.
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.visibility = 'visible';

      // For split-words, the animation target is the inner spans.
      // We need to ensure they are also set to their final state.
      if (el.tagName.toLowerCase() === 'split-words') {
        // The inner spans are created by the component's logic,
        // so we'll wait a moment for them to be added to the DOM.
        setTimeout(() => {
          el.querySelectorAll('.split-words__word > span').forEach(span => {
            span.style.opacity = '1';
            span.style.transform = 'none';
            span.style.visibility = 'visible';
          });
        }, 0);
      }
    } else {
      // It's the first time this element is seen. Add its key to the set
      // so we can identify it if it gets re-rendered.
      animatedElementKeys.add(key);
    }
  };

  const findAndProcessNodes = (container) => {
    const elements = container.querySelectorAll('split-words, [data-animate]');
    elements.forEach(el => processNode(el));
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) { // ELEMENT_NODE
          if (node.matches('split-words, [data-animate]')) {
            processNode(node);
          }
          findAndProcessNodes(node);
        }
      }
    }
  });

  // On initial page load, populate the set with all elements that will animate.
  document.addEventListener('DOMContentLoaded', () => {
    findAndProcessNodes(document.body);
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();

// Product Bundle Enhancements
// Visual slot-based bundle system with master/duplicate control
(function() {

  /**
   * Get total number of filled slots (visible product cards) in the bundle
   * This counts each visual card, including duplicates
   */
  function getTotalBundleSlots(bundle) {
    return bundle.variants.filter(v => !v.hasAttribute('available')).length;
  }

  /**
   * Get total quantity across all products
   * For duplicate-allowed mode, this equals the number of slots
   */
  function getTotalBundleQuantity(bundle) {
    if (bundle.preventDuplicate) {
      // Original behavior for prevent duplicate mode
      let total = 0;
      bundle.variants.forEach((variant) => {
        if (!variant.hasAttribute('available')) {
          const quantityInput = variant.querySelector('quantity-input');
          if (quantityInput && quantityInput.input) {
            total += parseInt(quantityInput.input.value) || 1;
          }
        }
      });
      return total;
    } else {
      // In duplicate-allowed mode, total quantity = number of filled slots
      return getTotalBundleSlots(bundle);
    }
  }

  /**
   * Find the master card for a given variant ID
   * The master is the first instance of that variant
   */
  function getMasterCard(bundle, variantId) {
    return Array.from(bundle.variants).find(v =>
      !v.hasAttribute('available') &&
      v.getAttribute('data-variant-id') === String(variantId) &&
      !v.hasAttribute('data-duplicate')
    );
  }

  /**
   * Get all duplicate cards for a given variant ID
   */
  function getDuplicateCards(bundle, variantId) {
    return Array.from(bundle.variants).filter(v =>
      !v.hasAttribute('available') &&
      v.getAttribute('data-variant-id') === String(variantId) &&
      v.hasAttribute('data-duplicate')
    );
  }

  /**
   * Count how many total cards (master + duplicates) exist for a variant
   */
  function getVariantCardCount(bundle, variantId) {
    return Array.from(bundle.variants).filter(v =>
      !v.hasAttribute('available') &&
      v.getAttribute('data-variant-id') === String(variantId)
    ).length;
  }

  /**
   * Update the master card's quantity input to reflect total count
   */
  function syncMasterQuantity(bundle, variantId) {
    const masterCard = getMasterCard(bundle, variantId);
    if (masterCard) {
      const quantityInput = masterCard.querySelector('quantity-input');
      if (quantityInput && quantityInput.input) {
        const totalCount = getVariantCardCount(bundle, variantId);
        quantityInput.input.value = totalCount;

        // Update validation
        if (typeof quantityInput.validateQtyRules === 'function') {
          quantityInput.validateQtyRules();
        }
      }
    }
  }

  /**
   * Recalculate master vs duplicate status for ALL cards
   * Simple rule: First card of each variant = MASTER, rest = DUPLICATE
   * This runs after every change, so position in DOM determines status
   */
  function styleDuplicateCards(bundle) {
    console.log('Recalculating master/duplicate status...');

    // Track which variant IDs we've seen and their product data
    const seenVariants = new Set();
    const variantDataMap = new Map(); // Store product data by variant ID

    // Go through all variants in DOM order
    bundle.variants.forEach(variant => {
      // Clean up empty slots
      if (variant.hasAttribute('available')) {
        if (variant.hasAttribute('data-duplicate')) {
          variant.removeAttribute('data-duplicate');
        }
        // Re-enable input on empty slots for when they get reused
        const quantityInput = variant.querySelector('quantity-input input');
        if (quantityInput && quantityInput.hasAttribute('disabled')) {
          quantityInput.removeAttribute('disabled');
        }
        return;
      }

      const variantId = variant.getAttribute('data-variant-id');

      // Collect product data from any card that has it
      // CRITICAL: Verify the data actually matches this variant ID
      if (variant.product && variant._variantData && !variantDataMap.has(variantId)) {
        // Double-check the variant data ID matches the card's variant ID
        if (String(variant._variantData.id) === String(variantId)) {
          variantDataMap.set(variantId, {
            product: variant.product,
            variant: variant._variantData
          });
        } else {
          console.error('ERROR: Card', variantId, 'has mismatched variant data', variant._variantData.id);
          // Clear the bad data
          delete variant.product;
          delete variant._variantData;
        }
      }

      // First occurrence of this variant = MASTER (remove duplicate attribute)
      // Subsequent occurrences = DUPLICATE (add duplicate attribute)
      if (!seenVariants.has(variantId)) {
        // This is the FIRST (master) card for this variant
        if (variant.hasAttribute('data-duplicate')) {
          variant.removeAttribute('data-duplicate');
          console.log('  → Variant', variantId, 'set as MASTER (first in DOM)');
        }

        // Enable quantity input on master cards (so it gets submitted with form)
        const quantityInput = variant.querySelector('quantity-input input');
        if (quantityInput) {
          quantityInput.removeAttribute('disabled');
        }

        seenVariants.add(variantId);
      } else {
        // This is a DUPLICATE card
        if (!variant.hasAttribute('data-duplicate')) {
          variant.setAttribute('data-duplicate', 'true');
          console.log('  → Variant', variantId, 'set as DUPLICATE');
        }

        // Disable quantity input on duplicate cards to prevent form submission
        // Disabled inputs are not submitted with forms in HTML
        const quantityInput = variant.querySelector('quantity-input input');
        if (quantityInput) {
          quantityInput.setAttribute('disabled', 'disabled');
          quantityInput.value = '1'; // Keep at 1 for internal tracking
        }
      }
    });

    // Second pass: ensure all cards have product data ONLY for their specific variant
    bundle.variants.forEach(variant => {
      if (variant.hasAttribute('available')) return;

      const variantId = variant.getAttribute('data-variant-id');
      const data = variantDataMap.get(variantId);

      if (data) {
        // Verify the data actually matches this variant before assigning
        if (String(data.variant.id) === String(variantId)) {
          if (!variant.product || !variant._variantData) {
            variant.product = data.product;
            variant._variantData = data.variant;
            console.log('  → Restored product data for variant', variantId);
          }
        } else {
          console.error('ERROR: Attempting to restore wrong data! Card:', variantId, 'Data:', data.variant.id);
        }
      }
    });

    console.log('  → Recalculation complete');
  }

  /**
   * Update bundle state: locking, progress bar, submit button
   * Based on total number of filled slots
   */
  function updateBundleState(bundle) {
    // Only apply custom logic when duplicates are allowed
    if (bundle.preventDuplicate) return;

    const totalSlots = getTotalBundleSlots(bundle);
    const bundleWrapper = bundle.closest('.product-bundle-wrapper');
    const isAtMax = totalSlots >= bundle.bundleMax;

    // Update bundle lock state based on total slots filled
    if (isAtMax) {
      if (bundleWrapper) bundleWrapper.setAttribute('locked', '');
    } else {
      if (bundleWrapper) bundleWrapper.removeAttribute('locked');
    }

    // Update progress bar based on slots filled vs bundle max
    const progressBarElement = bundle.querySelector('[data-product-bundle-progress-bar]');
    if (progressBarElement) {
      const percent = Math.min(100, (100 * totalSlots / bundle.bundleMax));
      progressBarElement.style.setProperty('--progress', `${percent}%`);
    }

    // Update submit button state - enable when minimum slots are filled
    if (totalSlots >= bundle.bundleMin) {
      bundle.submitButton.disabled = false;
    } else {
      bundle.submitButton.disabled = true;
    }

    // Update max attribute on master card quantity inputs
    bundle.variants.forEach(variant => {
      if (variant.hasAttribute('available') || variant.hasAttribute('data-duplicate')) return;

      const quantityInput = variant.querySelector('quantity-input');
      const input = quantityInput?.input;
      if (input) {
        const variantId = variant.getAttribute('data-variant-id');
        const currentCount = getVariantCardCount(bundle, variantId);
        const otherSlots = totalSlots - currentCount;
        const availableSlots = bundle.bundleMax - otherSlots;

        // Set max to how many more slots this variant can fill
        input.setAttribute('max', availableSlots);
        input.setAttribute('data-max', availableSlots);

        // Trigger button state update
        if (typeof quantityInput.validateQtyRules === 'function') {
          quantityInput.validateQtyRules();
        }

        console.log('Updated max for variant', variantId, 'to', availableSlots, '(current:', currentCount, 'total:', totalSlots, ')');
      }
    });

    // Style duplicate cards appropriately
    styleDuplicateCards(bundle);

    return { totalSlots, isAtMax };
  }

  /**
   * Calculate and display bundle savings
   * Shows compare price, savings amount, and percentage
   * Supports multiple calculation methods: compare_at_price, bundle_discount, or both
   */
  function updateSavingsDisplay(bundle) {
    console.log('=== UPDATING SAVINGS DISPLAY ===');

    const savingsHeader = bundle.querySelector('[data-bundle-savings]');
    const savingsCard = bundle.querySelector('[data-bundle-savings-card]');
    if (!savingsHeader || !savingsCard) {
      console.log('  → No savings container found (feature may be disabled in settings)');
      return;
    }

    // Get all filled variant cards (not available/empty)
    const allVariants = Array.from(bundle.variants);
    const filledVariants = allVariants.filter(v => !v.hasAttribute('available'));

    console.log('  → Total variants found:', allVariants.length);
    console.log('  → Available (empty) variants:', allVariants.filter(v => v.hasAttribute('available')).length);
    console.log('  → Filled variants:', filledVariants.length);

    // Debug: Show what's in each variant
    if (filledVariants.length > 0) {
      filledVariants.forEach((v, i) => {
        console.log(`    Variant ${i}:`, {
          hasAvailableAttr: v.hasAttribute('available'),
          variantId: v.getAttribute('data-variant-id'),
          hasProductData: !!v.product,
          hasVariantData: !!v._variantData
        });
      });
    }

    if (filledVariants.length === 0) {
      savingsHeader.setAttribute('hidden', '');
      savingsCard.setAttribute('hidden', '');
      console.log('  → No products in bundle, hiding savings');
      return;
    }

    // Check if bundle is complete (all slots filled)
    const maxSlots = parseInt(bundle.getAttribute('data-bundle-max')) || 3;
    const isComplete = filledVariants.length >= maxSlots;

    if (!isComplete) {
      savingsHeader.setAttribute('hidden', '');
      savingsCard.setAttribute('hidden', '');
      console.log(`  → Bundle not complete (${filledVariants.length}/${maxSlots}), hiding pricing card`);
      return;
    }

    console.log(`  → Bundle complete (${filledVariants.length}/${maxSlots}), proceeding with pricing display`);

    // Get savings calculation settings
    const savingsMethod = bundle.getAttribute('data-savings-method') || 'compare_at_price';
    const discountType = bundle.getAttribute('data-discount-type') || 'percentage';
    const bundleDiscountPercent = parseFloat(bundle.getAttribute('data-discount-percentage')) || 15;
    const bundleDiscountAmount = parseFloat(bundle.getAttribute('data-discount-amount')) || 10;
    const discountLevel = bundle.getAttribute('data-discount-level') || 'product';
    const roundingMode = bundle.getAttribute('data-rounding-mode') || 'standard';

    console.log('  → Savings method:', savingsMethod);
    console.log('  → Discount type:', discountType);
    console.log('  → Discount level:', discountLevel);
    console.log('  → Rounding mode:', roundingMode);
    if (discountType === 'percentage') {
      console.log('  → Discount value:', bundleDiscountPercent + '%');
    } else {
      console.log('  → Discount value: $' + bundleDiscountAmount);
    }

    let totalBundlePrice = 0;
    let variantsWithData = 0;

    // Calculate bundle price total
    filledVariants.forEach(variant => {
      if (!variant._variantData) {
        console.log('  ⚠️ Variant missing _variantData');
        return;
      }

      variantsWithData++;
      const variantData = variant._variantData;
      const quantity = 1; // Each slot represents qty 1

      // Get actual price
      totalBundlePrice += variantData.price * quantity;

      console.log('  → Variant', variantData.id, ':', {
        price: variantData.price,
        has_compare: !!variantData.compare_at_price
      });
    });

    console.log('  → Variants with data:', variantsWithData, '/', filledVariants.length);

    // Calculate savings based on selected method
    let totalComparePrice = 0;
    let savingsAmount = 0;
    let savingsPercentage = 0;

    if (savingsMethod === 'compare_at_price') {
      // Method 1: Use product compare_at_price values
      filledVariants.forEach(variant => {
        if (!variant._variantData) return;
        const variantData = variant._variantData;
        const compareAtPrice = variantData.compare_at_price || variantData.price;
        totalComparePrice += compareAtPrice;
      });

      savingsAmount = totalComparePrice - totalBundlePrice;
      savingsPercentage = totalComparePrice > 0
        ? Math.round((savingsAmount / totalComparePrice) * 100)
        : 0;

      console.log('  → Compare-at-price method:', {
        comparePrice: totalComparePrice,
        bundlePrice: totalBundlePrice,
        savings: savingsAmount,
        percentage: savingsPercentage
      });

    } else if (savingsMethod === 'bundle_discount') {
      // Method 2: Apply bundle discount (percentage or fixed amount)
      // Show savings OFF the current bundle total
      if (discountType === 'percentage') {
        // Regular price = bundle total (before discount)
        totalComparePrice = totalBundlePrice;

        if (discountLevel === 'product') {
          // PRODUCT-LEVEL: Discount applies to EACH item individually, then rounds to nearest cent
          // This matches Shopify's cart calculation exactly
          let discountedTotal = 0;
          filledVariants.forEach(variant => {
            if (!variant._variantData) return;
            const variantData = variant._variantData;
            const originalPriceInCents = variantData.price;

            // Calculate discounted price
            const discountMultiplier = 1 - (bundleDiscountPercent / 100);
            const exactDiscountedCents = (originalPriceInCents / 100) * discountMultiplier * 100;

            // Apply rounding mode
            let discountedPriceInCents;
            if (roundingMode === 'up') {
              discountedPriceInCents = Math.ceil(exactDiscountedCents);
            } else if (roundingMode === 'down') {
              discountedPriceInCents = Math.floor(exactDiscountedCents);
            } else if (roundingMode === 'up_to_99') {
              // Round up to nearest .99 (e.g., 17450 cents → 17499 cents)
              discountedPriceInCents = Math.floor(exactDiscountedCents / 100) * 100 + 99;
            } else if (roundingMode === 'down_to_99') {
              // Round down to nearest .99 (e.g., 17550 cents → 17499 cents)
              discountedPriceInCents = Math.floor((exactDiscountedCents - 1) / 100) * 100 + 99;
            } else {
              discountedPriceInCents = Math.round(exactDiscountedCents);
            }

            discountedTotal += discountedPriceInCents;

            console.log('    Item:', {
              originalCents: originalPriceInCents,
              originalDollars: (originalPriceInCents / 100),
              multiplier: discountMultiplier,
              exactCents: exactDiscountedCents,
              roundedCents: discountedPriceInCents,
              roundingMode: roundingMode,
              finalPrice: '$' + (discountedPriceInCents / 100).toFixed(2)
            });
          });

          // Savings = difference between original total and discounted total
          savingsAmount = totalBundlePrice - discountedTotal;
          savingsPercentage = bundleDiscountPercent;

          console.log('  → Product-level discount (item-by-item):', {
            originalTotal: totalBundlePrice,
            discountedTotal: discountedTotal,
            savings: savingsAmount,
            finalPrice: '$' + (discountedTotal / 100).toFixed(2)
          });
        } else {
          // ORDER-LEVEL: Discount applies to total, rounds once
          // Example: $249.97 × 70% = $174.98 (rounds total)
          const finalPrice = Math.round(totalBundlePrice * (1 - bundleDiscountPercent / 100));
          savingsAmount = totalBundlePrice - finalPrice;
          savingsPercentage = bundleDiscountPercent;

          console.log('  → Order-level discount (total):', {
            originalTotal: totalBundlePrice,
            finalPrice: finalPrice,
            savings: savingsAmount
          });
        }
      } else {
        // Fixed dollar amount - convert to cents (Shopify prices are in cents)
        const discountInCents = bundleDiscountAmount * 100;
        // Regular price = bundle total
        totalComparePrice = totalBundlePrice;
        // Savings = fixed amount
        savingsAmount = discountInCents;
        savingsPercentage = totalBundlePrice > 0
          ? Math.round((savingsAmount / totalBundlePrice) * 100)
          : 0;
      }

      console.log('  → Bundle discount method (' + discountType + '):', {
        comparePrice: totalComparePrice,
        bundlePrice: totalBundlePrice,
        savings: savingsAmount,
        percentage: savingsPercentage
      });

      console.log('  → Savings check: savingsAmount =', savingsAmount, ', is it > 0?', savingsAmount > 0);

    } else if (savingsMethod === 'both') {
      // Method 3: Simple calculation - show bundle discount savings only
      // No final price displayed, so no need for complex stacking or rounding

      // Regular price = current sum of items (including any sale prices)
      totalComparePrice = totalBundlePrice;

      // Calculate savings as simple percentage of current total
      if (discountType === 'percentage') {
        savingsAmount = Math.round(totalBundlePrice * (bundleDiscountPercent / 100));
        savingsPercentage = bundleDiscountPercent;
      } else {
        // Fixed dollar amount
        const discountInCents = bundleDiscountAmount * 100;
        savingsAmount = discountInCents;
        savingsPercentage = totalBundlePrice > 0
          ? Math.round((savingsAmount / totalBundlePrice) * 100)
          : 0;
      }

      console.log('  → Simplified savings (no final price):', {
        bundleTotal: totalBundlePrice,
        discountType: discountType,
        discountValue: discountType === 'percentage' ? bundleDiscountPercent + '%' : '$' + bundleDiscountAmount,
        savingsAmount: savingsAmount,
        regularPrice: '$' + (totalBundlePrice / 100).toFixed(2),
        youSave: '$' + (savingsAmount / 100).toFixed(2),
        note: 'Final price shown in cart only'
      });
    }

    // Only show if there are actual savings
    console.log('  → Final check before display: savingsAmount =', savingsAmount);
    if (savingsAmount <= 0) {
      savingsHeader.setAttribute('hidden', '');
      savingsCard.setAttribute('hidden', '');
      console.log('  → No savings to show, hiding container');
      return;
    }

    console.log('  ✓ Savings amount is positive, proceeding to display...');

    // Update UI elements with new unified structure selectors
    const heroPrice = savingsCard.querySelector('.bundle-pricing-card__hero-price');
    const regularPrice = savingsCard.querySelector('.bundle-pricing-card__value-amount');
    const savingsAmountElement = savingsCard.querySelector('[data-bundle-savings-amount]');
    const savingsPercentageElement = savingsCard.querySelector('[data-bundle-savings-percentage]');

    console.log('  → Updating UI elements...');

    // Helper function to format money (fallback if theme.Currency doesn't exist)
    function formatMoney(cents) {
      try {
        if (typeof theme !== 'undefined' && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
          return theme.Currency.formatMoney(cents);
        }
      } catch(e) {
        console.warn('theme.Currency.formatMoney error:', e);
      }
      // Fallback: simple formatting
      const dollars = (cents / 100).toFixed(2);
      return '$' + dollars;
    }

    // Calculate and apply .99 rounding to the final displayed price if selected
    let discountedPrice = totalBundlePrice - savingsAmount;

    if (roundingMode === 'up_to_99') {
      // Round up to nearest .99
      discountedPrice = Math.floor(discountedPrice / 100) * 100 + 99;
      // Recalculate savings based on rounded price
      savingsAmount = totalBundlePrice - discountedPrice;
      console.log('    - Applied up_to_99 rounding to final price');
    } else if (roundingMode === 'down_to_99') {
      // Round down to nearest .99
      discountedPrice = Math.floor((discountedPrice - 1) / 100) * 100 + 99;
      // Recalculate savings based on rounded price
      savingsAmount = totalBundlePrice - discountedPrice;
      console.log('    - Applied down_to_99 rounding to final price');
    }

    if (heroPrice) {
      // Show the final price after discount (matching cart total) - this is the hero element
      const formatted = formatMoney(discountedPrice);
      heroPrice.textContent = formatted;
      console.log('    - Your Bundle Price (hero):', formatted);
    }

    if (regularPrice) {
      // Show the regular price (before discount)
      const formatted = formatMoney(totalBundlePrice);
      regularPrice.textContent = formatted;
      console.log('    - Regular Price:', formatted);
    }

    if (savingsAmountElement) {
      // Update savings amount with recalculated value
      const formatted = formatMoney(savingsAmount);
      savingsAmountElement.textContent = formatted;
      console.log('    - Savings amount (adjusted):', formatted);
    }

    if (savingsPercentageElement) {
      savingsPercentageElement.textContent = `${savingsPercentage}% OFF`;
      console.log('    - Savings percentage:', savingsPercentage + '% OFF');
    }

    // Show the savings header and card
    console.log('  → Removing hidden attributes...');
    console.log('    - Header had hidden before:', savingsHeader.hasAttribute('hidden'));
    console.log('    - Card had hidden before:', savingsCard.hasAttribute('hidden'));
    savingsHeader.removeAttribute('hidden');
    savingsCard.removeAttribute('hidden');
    console.log('    - Header has hidden after:', savingsHeader.hasAttribute('hidden'));
    console.log('    - Card has hidden after:', savingsCard.hasAttribute('hidden'));

    // Force display in case CSS is interfering
    savingsHeader.style.display = '';
    savingsHeader.style.visibility = '';
    savingsHeader.style.opacity = '';
    savingsCard.style.display = '';
    savingsCard.style.visibility = '';
    savingsCard.style.opacity = '';

    // Note: Disclaimer visibility is now controlled by admin setting in Liquid
    // No need to show/hide it via JavaScript
    const disclaimer = savingsCard.querySelector('.bundle-pricing-card__disclaimer');
    if (disclaimer) {
      console.log('    - Disclaimer is present (controlled by admin setting)');
    }

    console.log('  ✓ Savings display updated and shown');
  }

  /**
   * Reorder all slots to maintain visual consistency
   * Master cards come first, followed by their duplicates
   * Uses CSS order property and ensures visual grouping
   */
  function reorderSlots(bundle) {
    console.log('=== REORDERING SLOTS ===');

    // First, reset ALL variants to high order and collect filled ones
    const allVariants = Array.from(bundle.variants);
    const filledVariants = [];

    allVariants.forEach(variant => {
      if (variant.hasAttribute('available')) {
        // Available (empty) slots get pushed to the end
        variant.style.order = 9999;
      } else {
        filledVariants.push(variant);
      }
    });

    console.log('Found', filledVariants.length, 'filled slots');

    // Group filled variants by variant ID
    const groups = {};
    const insertionOrder = []; // Track order of first appearance

    filledVariants.forEach(variant => {
      const variantId = variant.getAttribute('data-variant-id');

      if (!groups[variantId]) {
        groups[variantId] = { master: null, duplicates: [], firstSeen: insertionOrder.length };
        insertionOrder.push(variantId);
      }

      if (variant.hasAttribute('data-duplicate')) {
        groups[variantId].duplicates.push(variant);
      } else {
        groups[variantId].master = variant;
      }
    });

    console.log('Grouped into', insertionOrder.length, 'unique variants');

    // Assign sequential order values starting from 0
    let order = 0;
    insertionOrder.forEach(variantId => {
      const group = groups[variantId];
      if (group.master) {
        // Master gets current order
        group.master.style.order = order;
        console.log('  → Variant', variantId, 'Master at order:', order);
        order++;

        // Duplicates get sequential orders immediately after
        group.duplicates.forEach((dup, idx) => {
          dup.style.order = order;
          console.log('  → Variant', variantId, 'Duplicate', idx + 1, 'at order:', order);
          order++;
        });
      }
    });

    console.log('Total slots ordered:', order);
    console.log('=== REORDERING COMPLETE ===');
  }

  /**
   * Handle quantity changes on master cards
   * When master quantity changes, add/remove duplicate slots
   */
  function handleMasterQuantityChange(bundle, masterCard) {
    if (bundle.preventDuplicate) return;

    const variantId = masterCard.getAttribute('data-variant-id');
    const quantityInput = masterCard.querySelector('quantity-input');
    if (!quantityInput || !quantityInput.input) return;

    const desiredQuantity = parseInt(quantityInput.input.value) || 1;
    const currentCount = getVariantCardCount(bundle, variantId);
    const totalSlots = getTotalBundleSlots(bundle);

    // Calculate how many slots we can actually fill
    const availableSlots = bundle.bundleMax - (totalSlots - currentCount);
    const actualQuantity = Math.min(desiredQuantity, Math.max(1, availableSlots + currentCount));

    // Update the input to reflect what's actually possible
    if (actualQuantity !== desiredQuantity) {
      quantityInput.input.value = actualQuantity;
      // Trigger validation
      if (typeof quantityInput.validateQtyRules === 'function') {
        quantityInput.validateQtyRules();
      }
    }

    const difference = actualQuantity - currentCount;

    if (difference > 0) {
      // Need to add duplicate slots
      let productCard = masterCard.product;
      let variantData = masterCard._variantData;

      // If master doesn't have data, try to get it from any card with same variant
      if (!productCard || !variantData) {
        console.log('  → Master missing data, searching other cards with variant', variantId);
        const allCards = Array.from(bundle.variants).filter(v =>
          !v.hasAttribute('available') &&
          v.getAttribute('data-variant-id') === String(variantId)
        );

        console.log('  → Found', allCards.length, 'cards with this variant ID');

        for (const card of allCards) {
          if (card.product && card._variantData) {
            // Verify the variant data matches the variant ID we're looking for
            if (String(card._variantData.id) === String(variantId)) {
              productCard = card.product;
              variantData = card._variantData;
              // Store on master for next time
              masterCard.product = productCard;
              masterCard._variantData = variantData;
              console.log('  → Found matching product data:', productCard.title || 'Unknown');
              break;
            } else {
              console.warn('  → Card has wrong variant data! Card variantId:', card.getAttribute('data-variant-id'), 'Data variantId:', card._variantData.id);
            }
          }
        }
      }

      if (productCard && variantData) {
        // Double-check that variant IDs match to prevent cross-product contamination
        if (String(variantData.id) !== String(variantId)) {
          console.error('ERROR: Variant ID mismatch! Master card has', variantId, 'but trying to add', variantData.id);
          return;
        }

        console.log('Adding', difference, 'duplicate cards for variant', variantId);
        console.log('  → Product:', productCard.title || 'Unknown');
        console.log('  → Variant ID:', variantData.id);

        for (let i = 0; i < difference; i++) {
          // Use the overridden addVariant which handles duplicate logic
          bundle.addVariant(productCard, variantData);
        }
      } else {
        console.error('Cannot add duplicates - missing product data for variant', variantId);
      }
    } else if (difference < 0) {
      // Need to remove duplicate slots
      const duplicates = getDuplicateCards(bundle, variantId);
      const toRemove = Math.abs(difference);

      console.log('Removing', toRemove, 'duplicates via quantity decrease');
      for (let i = 0; i < Math.min(toRemove, duplicates.length); i++) {
        bundle.clearVariant(duplicates[i]);
      }

      // After removing, force reorder multiple times with delays
      setTimeout(() => {
        reorderSlots(bundle);
        styleDuplicateCards(bundle);
        updateBundleState(bundle);

        // Trigger bundle's native update methods
        if (bundle.updateTotal) bundle.updateTotal();
        if (bundle.updateTotalWithCurrency) bundle.updateTotalWithCurrency();
        if (bundle.updateProgressBar) bundle.updateProgressBar();

        // Double-check reordering after a moment
        setTimeout(() => {
          reorderSlots(bundle);
        }, 100);
      }, 50);
    }

    // Note: When adding (difference > 0), the addVariant override handles updates
    // Force a final reorder to be safe
    setTimeout(() => {
      reorderSlots(bundle);

      // Trigger the bundle's native update methods after quantity changes
      if (bundle.updateTotal) bundle.updateTotal();
      if (bundle.updateTotalWithCurrency) bundle.updateTotalWithCurrency();
      if (bundle.updateProgressBar) bundle.updateProgressBar();
    }, 200);
  }

  function initBundleEnhancements() {
    const productBundles = document.querySelectorAll('product-bundle');

    productBundles.forEach(bundle => {
      // Skip if already initialized
      if (bundle.hasAttribute('data-bundle-slots-initialized')) return;
      bundle.setAttribute('data-bundle-slots-initialized', 'true');

      console.log('Initializing bundle enhancements, preventDuplicate:', bundle.preventDuplicate);

      // ========================================================================
      // UNIVERSAL: Savings display - works regardless of duplicate settings
      // ========================================================================

      function updateSavingsForBundle() {
        console.log('Universal savings update triggered');
        updateSavingsDisplay(bundle);
      }

      // Listen for bundle changes (works for all bundle modes)
      bundle.addEventListener('bundle:added', function(event) {
        console.log('bundle:added event fired');

        // CRITICAL: Store variant data on the card for savings calculation
        // This works for BOTH duplicate and non-duplicate modes
        if (event.detail && event.detail.product && event.detail.variant) {
          setTimeout(() => {
            const variantId = event.detail.variant.id;
            const variantCards = Array.from(bundle.variants).filter(v =>
              !v.hasAttribute('available') &&
              v.getAttribute('data-variant-id') === String(variantId)
            );

            variantCards.forEach(card => {
              card.product = event.detail.product;
              card._variantData = event.detail.variant;
              console.log('  → Stored variant data for savings calculation:', variantId);
            });

            updateSavingsForBundle();
          }, 100);
        } else {
          setTimeout(() => updateSavingsForBundle(), 150);
        }
      });

      bundle.addEventListener('bundle:removed', function() {
        console.log('bundle:removed event fired');
        setTimeout(() => updateSavingsForBundle(), 150);
      });

      // Initial savings display update
      setTimeout(() => {
        console.log('Initial savings display update');
        updateSavingsForBundle();
      }, 200);

      // Additional check: Watch for any DOM changes in the bundle
      const bundleObserver = new MutationObserver(() => {
        console.log('Bundle DOM changed, updating savings');
        updateSavingsForBundle();
      });

      const bundleBody = bundle.querySelector('.product-bundle__body');
      if (bundleBody) {
        bundleObserver.observe(bundleBody, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['available']
        });
      }

      // Listen for native change events too
      bundle.addEventListener('change', function() {
        console.log('Bundle change event detected');
        setTimeout(() => updateSavingsForBundle(), 100);
      });

      // ========================================================================
      // SLOT SYSTEM: Only for duplicate-allowed mode
      // ========================================================================

      // Override the original updateProgressBar method when duplicates are allowed
      if (!bundle.preventDuplicate && typeof bundle.updateProgressBar === 'function') {
        bundle.updateProgressBar = function() {
          // Use total slots for progress calculation
          const totalSlots = getTotalBundleSlots(this);
          const percent = Math.min(100, (100 * totalSlots / this.bundleMax));
          const element = this.querySelector('[data-product-bundle-progress-bar]');
          if (element) {
            element.style.setProperty('--progress', `${percent}%`);
          }
        };
      }

      /**
       * Override addVariant to handle duplicate products in bundle
       * After each addition, recalculate master/duplicate status based on DOM order
       * Simple rule: First card of each variant ID = master, rest = duplicates
       */
      if (!bundle.preventDuplicate && typeof bundle.addVariant === 'function') {
        const originalAddVariant = bundle.addVariant.bind(bundle);

        // Store reference to original for use in other functions
        bundle.addVariant._original = originalAddVariant;

        bundle.addVariant = function(product, variant) {
          const totalSlots = getTotalBundleSlots(this);

          // Check if we're at max capacity
          if (totalSlots >= this.bundleMax) {
            return; // Can't add more
          }

          // Add the variant (whether it's new or duplicate)
          originalAddVariant.call(this, product, variant);

          // After adding, find the newly added card and store product/variant data
          setTimeout(() => {
            console.log('Added variant', variant.id, '- storing product data and recalculating');

            // Find ALL cards for this variant and ensure they all have product data
            const allCardsForVariant = Array.from(this.variants).filter(v =>
              !v.hasAttribute('available') &&
              v.getAttribute('data-variant-id') === String(variant.id)
            );

            // Store product data on ALL cards of this variant FIRST
            // This ensures data persists even when master status changes
            // CRITICAL: Only store data that matches the card's variant ID
            allCardsForVariant.forEach(card => {
              const cardVariantId = card.getAttribute('data-variant-id');
              // Verify the variant data matches the card's variant ID
              if (String(variant.id) === String(cardVariantId)) {
                card.product = product;
                card._variantData = variant;
              } else {
                console.error('ERROR: Trying to store wrong variant data! Card:', cardVariantId, 'Data:', variant.id);
              }
            });
            console.log('  → Stored product data on', allCardsForVariant.length, 'card(s) for variant', variant.id);

            // Recalculate which cards are masters vs duplicates
            styleDuplicateCards(this);

            // Update master card quantity if this variant has multiple cards
            syncMasterQuantity(this, variant.id);

            // Reorder and update state
            reorderSlots(this);
            updateBundleState(this);

            // Attach listeners AFTER product data is stored with a small delay
            setTimeout(() => {
              attachSlotListeners();
            }, 50);
          }, 50);
        };
      }

      /**
       * Attach listeners to monitor master card quantity changes
       * and handle duplicate card interactions
       */
      function attachSlotListeners() {
        console.log('=== ATTACHING SLOT LISTENERS ===');

        // Remove old listeners to prevent duplicates
        bundle.variants.forEach(variant => {
          if (variant.hasAttribute('available')) return;

          const quantityInput = variant.querySelector('quantity-input');
          if (quantityInput) {
            quantityInput.removeAttribute('data-slot-listener');
          }

          const removeButton = variant.querySelector('product-bundle-remove-button');
          if (removeButton) {
            removeButton.removeAttribute('data-slot-listener');
          }
        });

        // Attach fresh listeners
        bundle.variants.forEach(variant => {
          if (variant.hasAttribute('available')) return;

          const isDuplicate = variant.hasAttribute('data-duplicate');
          const variantId = variant.getAttribute('data-variant-id');

          if (!isDuplicate) {
            // Master card - listen for quantity changes
            const quantityInput = variant.querySelector('quantity-input');
            if (quantityInput && !quantityInput.hasAttribute('data-slot-listener')) {
              quantityInput.setAttribute('data-slot-listener', 'true');

              const inputElement = quantityInput.querySelector('input');
              if (inputElement) {
                // Check if product data is available
                if (!variant.product || !variant._variantData) {
                  console.warn('  ⚠️ MASTER variant', variantId, 'missing product data! Attempting to find it...');
                  // Try to find and store product data from the original addVariant
                  const allCards = Array.from(bundle.variants).filter(v =>
                    !v.hasAttribute('available') &&
                    v.getAttribute('data-variant-id') === String(variantId) &&
                    v.product && v._variantData
                  );
                  if (allCards.length > 0 && String(allCards[0]._variantData.id) === String(variantId)) {
                    variant.product = allCards[0].product;
                    variant._variantData = allCards[0]._variantData;
                    console.log('  ✓ Restored product data:', allCards[0].product.title || 'Unknown');
                  } else {
                    console.error('  ✗ Could not find product data for variant', variantId);
                  }
                }

                if (variant.product && variant._variantData) {
                  console.log('  → Attaching listener to MASTER variant', variantId, '-', variant.product.title || 'Unknown');
                } else {
                  console.error('  ✗ MASTER variant', variantId, 'STILL missing product data after search!');
                }

                // Single handler for all quantity changes
                const handleChange = function(e) {
                  // Small delay to ensure value is updated
                  setTimeout(() => {
                    console.log('  ✓ Quantity changed for variant:', variantId, 'New value:', inputElement.value);
                    handleMasterQuantityChange(bundle, variant);
                  }, 100);
                };

                // Listen for the native change event that fires after +/- buttons
                inputElement.addEventListener('change', handleChange);
                inputElement.addEventListener('input', handleChange);
              } else {
                console.warn('  ✗ Could not find input element in quantity-input for variant', variantId);
              }
            }
          }

          // Handle remove button - works for both master and duplicates
          const removeButton = variant.querySelector('product-bundle-remove-button');
          if (removeButton && !removeButton.hasAttribute('data-slot-listener')) {
            removeButton.setAttribute('data-slot-listener', 'true');

            removeButton.addEventListener('click', function(e) {
              console.log('Remove button clicked for variant:', variantId);

              // After removal, recalculate everything
              // The first remaining card of this variant will automatically become master
              setTimeout(() => {
                const remainingCards = Array.from(bundle.variants).filter(v =>
                  !v.hasAttribute('available') &&
                  v.getAttribute('data-variant-id') === variantId
                );

                // If there are remaining cards, sync the master's quantity
                if (remainingCards.length > 0) {
                  syncMasterQuantity(bundle, variantId);
                }

                // Recalculate master/duplicate, reorder, and update state
                styleDuplicateCards(bundle);
                reorderSlots(bundle);
                updateBundleState(bundle);
                attachSlotListeners();
              }, 100);
            });
          }
        });

        // Summary log
        const masterCount = Array.from(bundle.variants).filter(v =>
          !v.hasAttribute('available') && !v.hasAttribute('data-duplicate')
        ).length;
        const duplicateCount = Array.from(bundle.variants).filter(v =>
          !v.hasAttribute('available') && v.hasAttribute('data-duplicate')
        ).length;
        console.log('=== LISTENERS ATTACHED:', masterCount, 'masters,', duplicateCount, 'duplicates ===');
      }

      // Universal reorder function that handles all updates
      function performFullUpdate() {
        reorderSlots(bundle);
        styleDuplicateCards(bundle);
        if (window.updateBundleState) {
          window.updateBundleState(bundle); // Gamification system
        }
        updateSavingsDisplay(bundle); // Always update savings regardless of bundle settings
        attachSlotListeners();
      }

      // Add event listeners for bundle-level changes
      bundle.addEventListener('change', function() {
        setTimeout(() => performFullUpdate(), 100);
      });

      bundle.addEventListener('bundle:added', function() {
        setTimeout(() => performFullUpdate(), 100);
      });

      bundle.addEventListener('bundle:removed', function() {
        setTimeout(() => performFullUpdate(), 100);
      });

      // Watch for DOM changes and update accordingly
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;

        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'available') {
            shouldUpdate = true;
          }
          if (mutation.type === 'childList') {
            shouldUpdate = true;
          }
        });

        if (shouldUpdate) {
          // Recalculate master/duplicate status and reorder after any change
          setTimeout(() => performFullUpdate(), 100);
        }
      });

      observer.observe(bundle.querySelector('.product-bundle__body'), {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['available']
      });

      // Initialize
      setTimeout(() => {
        performFullUpdate();
      }, 100);
    });
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleEnhancements);
  } else {
    initBundleEnhancements();
  }

  // Handle dynamically added bundles (e.g., in Shopify theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function() {
      setTimeout(initBundleEnhancements, 100);
    });
  }

  // Global test function - run this in console: testBundleSavings()
  window.testBundleSavings = function() {
    console.log('========== TESTING BUNDLE SAVINGS ==========');
    const bundle = document.querySelector('product-bundle');
    if (!bundle) {
      console.error('❌ No bundle element found!');
      return;
    }
    console.log('✓ Bundle element found:', bundle);
    console.log('  - preventDuplicate:', bundle.preventDuplicate);
    console.log('  - bundleMin:', bundle.bundleMin);
    console.log('  - bundleMax:', bundle.bundleMax);

    // Check variants
    const allVariants = Array.from(bundle.variants);
    console.log('  - Total variants:', allVariants.length);
    const filledVariants = allVariants.filter(v => !v.hasAttribute('available'));
    console.log('  - Filled variants:', filledVariants.length);
    const availableVariants = allVariants.filter(v => v.hasAttribute('available'));
    console.log('  - Available (empty) variants:', availableVariants.length);

    if (filledVariants.length > 0) {
      console.log('  - Filled variant details:');
      filledVariants.forEach((v, i) => {
        console.log(`    [${i}]`, {
          variantId: v.getAttribute('data-variant-id'),
          hasProduct: !!v.product,
          hasVariantData: !!v._variantData,
          price: v._variantData?.price || 'no data'
        });
      });
    }

    const savingsContainer = bundle.querySelector('[data-bundle-savings]');
    if (!savingsContainer) {
      console.error('❌ No savings container found!');
      console.log('Bundle footer HTML:', bundle.querySelector('.product-bundle__footer')?.innerHTML.substring(0, 500));
      return;
    }
    console.log('✓ Savings container found:', savingsContainer);
    console.log('  - Has hidden attribute:', savingsContainer.hasAttribute('hidden'));
    console.log('  - Computed display:', window.getComputedStyle(savingsContainer).display);
    console.log('  - Computed visibility:', window.getComputedStyle(savingsContainer).visibility);
    console.log('  - Class:', savingsContainer.className);

    // Force show for testing
    console.log('⚡ Forcing display...');
    savingsContainer.removeAttribute('hidden');
    savingsContainer.style.display = 'flex';
    savingsContainer.style.visibility = 'visible';
    savingsContainer.style.opacity = '1';

    const compareEl = savingsContainer.querySelector('[data-bundle-compare-price]');
    const savingsEl = savingsContainer.querySelector('[data-bundle-savings-amount]');
    const percentEl = savingsContainer.querySelector('[data-bundle-savings-percentage]');

    if (compareEl) compareEl.textContent = '$199.99';
    if (savingsEl) savingsEl.textContent = '$50.00';
    if (percentEl) percentEl.textContent = '30% OFF';

    console.log('✓ Forced savings display - check sidebar now!');
    console.log('========================================');
  };

  console.log('Bundle enhancements loaded. Run testBundleSavings() to test manually.');
})();

// ================================================================================
// GLOBAL FIX: Remove "Default Title" from Product Bundles
// ================================================================================
(function() {
  'use strict';

  /**
   * Clean up "Default Title" text from product bundle variant displays
   * This runs globally for all product bundles on the page
   */
  function removeDefaultTitleFromBundles() {
    // Find all product bundle variant cards
    const variantCards = document.querySelectorAll('[data-product-bundle-variant]');

    variantCards.forEach(card => {
      // Find all list items that might contain "Default Title"
      const optionItems = card.querySelectorAll('li');

      optionItems.forEach(item => {
        const text = item.textContent.trim();
        // Remove the list item if it only contains "Default Title"
        if (text === 'Default Title') {
          item.remove();
        }
      });

      // If the ul is now empty, remove it too
      const optionLists = card.querySelectorAll('ul');
      optionLists.forEach(list => {
        if (list.children.length === 0) {
          list.remove();
        }
      });
    });
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeDefaultTitleFromBundles);
  } else {
    removeDefaultTitleFromBundles();
  }

  // Listen for bundle events and clean up after products are added
  document.addEventListener('bundle:added', function() {
    setTimeout(removeDefaultTitleFromBundles, 100);
  });

  // Watch for DOM changes in product bundles using MutationObserver
  const observer = new MutationObserver(function(mutations) {
    let shouldClean = false;
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1 && (node.hasAttribute('data-product-bundle-variant-content') || node.textContent.includes('Default Title'))) {
            shouldClean = true;
          }
        });
      }
    });
    if (shouldClean) {
      setTimeout(removeDefaultTitleFromBundles, 50);
    }
  });

  // Observe all product bundle containers
  document.querySelectorAll('product-bundle').forEach(bundle => {
    observer.observe(bundle, {
      childList: true,
      subtree: true
    });
  });

  console.log('Default Title cleanup initialized for product bundles');
})();

// ==========================================
// BUNDLE CELEBRATION ANIMATION SYSTEM
// Modern, Apple-like micro-interactions
// ==========================================

class BundleCelebration {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.particles = [];
    this.animationId = null;
  }

  // Create canvas overlay
  createCanvas() {
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
    `;
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    document.body.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');
  }

  // Heart explosion effect (perfect for intimate products)
  triggerHeartExplosion(x, y) {
    if (!this.canvas) this.createCanvas();

    const colors = ['#ec4899', '#f472b6', '#fb7185', '#f43f5e'];
    const particleCount = 30;

    for (let i = 0; i < particleCount; i++) {
      this.particles.push({
        x: x,
        y: y,
        vx: (Math.random() - 0.5) * 10,
        vy: (Math.random() - 0.5) * 10 - 3,
        size: Math.random() * 20 + 10,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 1,
        decay: Math.random() * 0.015 + 0.01,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2
      });
    }

    this.animate();
  }

  // Animation loop
  animate() {
    if (this.particles.length === 0) {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
      if (this.canvas) {
        this.canvas.remove();
        this.canvas = null;
      }
      return;
    }

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.particles = this.particles.filter(p => {
      // Update physics
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.2; // Gravity
      p.rotation += p.rotationSpeed;
      p.life -= p.decay;

      if (p.life <= 0) return false;

      // Draw heart
      this.ctx.save();
      this.ctx.translate(p.x, p.y);
      this.ctx.rotate(p.rotation);
      this.ctx.globalAlpha = p.life;
      this.drawHeart(p.size, p.color);
      this.ctx.restore();

      return true;
    });

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  // Draw heart shape
  drawHeart(size, color) {
    this.ctx.fillStyle = color;
    this.ctx.beginPath();

    const x = 0;
    const y = 0;
    const width = size;
    const height = size;

    this.ctx.moveTo(x, y + height / 4);
    this.ctx.bezierCurveTo(x, y, x - width / 2, y, x - width / 2, y + height / 4);
    this.ctx.bezierCurveTo(x - width / 2, y + height / 2, x, y + height * 0.75, x, y + height);
    this.ctx.bezierCurveTo(x, y + height * 0.75, x + width / 2, y + height / 2, x + width / 2, y + height / 4);
    this.ctx.bezierCurveTo(x + width / 2, y, x, y, x, y + height / 4);

    this.ctx.fill();
  }
}

// Initialize globally
window.bundleCelebration = new BundleCelebration();

// ==========================================
// MILESTONE TOAST NOTIFICATIONS
// ==========================================

function showMilestoneToast(message, duration = 2000) {
  const toast = document.createElement('div');
  toast.className = 'bundle-milestone-toast';
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    top: 24px;
    left: 50%;
    transform: translateX(-50%) translateY(-80px);
    background: rgb(var(--color-foreground));
    color: rgb(var(--color-background));
    padding: 14px 28px;
    border-radius: var(--rounded-full);
    font-size: 14px;
    font-weight: 600;
    z-index: 10000;
    opacity: 0;
    transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 10px 40px rgb(0 0 0 / 0.3);
  `;

  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.style.transform = 'translateX(-50%) translateY(0)';
    toast.style.opacity = '1';
  });

  setTimeout(() => {
    toast.style.transform = 'translateX(-50%) translateY(-80px)';
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 500);
  }, duration);
}

// ==========================================
// INTEGRATED BUNDLE STATE & GAMIFICATION
// ==========================================

// Global function to update bundle state (optimized for performance & accessibility)
window.updateBundleState = function(bundle) {
  if (!bundle) {
    console.error('❌ updateBundleState called with no bundle element');
    return;
  }

  // Count products the same way reorderSlots does - by checking available attribute
  const allVariants = Array.from(bundle.querySelectorAll('[data-product-bundle-variant]'));

  console.log(`🔍 DEBUG: Found ${allVariants.length} total variant slots`);

  // A slot is filled if it does NOT have the 'available' attribute
  const filledSlots = allVariants.filter(variant => !variant.hasAttribute('available')).length;

  console.log(`  Filled slots (no 'available' attr): ${filledSlots}`);

  const maxSlots = parseInt(bundle.getAttribute('data-product-quantity')) || 3;
  const remaining = Math.max(0, maxSlots - filledSlots);
  const progress = Math.min(100, (filledSlots / maxSlots) * 100);

  console.log(`🎯 Bundle: ${filledSlots}/${maxSlots} (${progress.toFixed(0)}%)`);

  // Update progress bar
  const progressBar = bundle.querySelector('[data-product-bundle-progress-bar]');
  if (progressBar) {
    progressBar.style.setProperty('--progress', `${progress}%`);
    console.log('  ✅ Progress bar updated');
  } else {
    console.warn('  ⚠️ Progress bar not found');
  }

  // Update ARIA progressbar
  const progressContainer = bundle.querySelector('[data-bundle-progress-container]');
  if (progressContainer) {
    progressContainer.setAttribute('aria-valuenow', filledSlots);
  }

  // Update progress label (dynamic countdown)
  const progressLabel = bundle.querySelector('[data-bundle-progress-label]');
  if (progressLabel) {
    const messages = {
      0: `Pick <strong>${maxSlots}</strong> to unlock savings`,
      [maxSlots - 1]: `Pick <strong>1</strong> more to unlock! 🎯`,
      [maxSlots]: `Bundle complete! 🎊`
    };
    progressLabel.innerHTML = messages[filledSlots] || `Pick <strong>${remaining}</strong> more to unlock savings`;
    console.log('  ✅ Label updated:', progressLabel.textContent);
  } else {
    console.warn('  ⚠️ Progress label not found');
  }

  // Update progress count
  const progressCount = bundle.querySelector('[data-bundle-progress-count]');
  if (progressCount) {
    progressCount.textContent = `${filledSlots}/${maxSlots}`;
    console.log('  ✅ Count updated:', progressCount.textContent);
  } else {
    console.warn('  ⚠️ Progress count not found');
  }

  // Update progress dots (batch DOM updates)
  const dots = bundle.querySelectorAll('.bundle-progress-dot');
  console.log(`  📍 Found ${dots.length} dots`);
  dots.forEach((dot, index) => dot.classList.toggle('filled', index < filledSlots));

  // Update complete state
  const freeShippingBar = bundle.querySelector('.free-shipping-bar');
  const addButton = bundle.querySelector('[data-product-bundle-submit]');
  const isComplete = filledSlots >= maxSlots;
  const wasComplete = freeShippingBar?.classList.contains('free-shipping-bar--complete');

  if (freeShippingBar) {
    freeShippingBar.classList.toggle('free-shipping-bar--complete', isComplete);
    console.log(`  ${isComplete ? '🎉' : '⏳'} Complete state: ${isComplete}`);
  }

  if (addButton) {
    addButton.classList.toggle('bundle-complete', isComplete);
    console.log(`  ${isComplete ? '✨' : '💤'} Button glow: ${isComplete}`);
  }

  // Show milestone toasts (respect prefers-reduced-motion)
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!prefersReducedMotion) {
    if (!wasComplete && isComplete) {
      console.log('  🎊 COMPLETE! Showing toast...');
      showMilestoneToast('Bundle complete! 🎊');
    } else if (filledSlots === 1 && !bundle.dataset.toast1) {
      bundle.dataset.toast1 = 'true';
      showMilestoneToast('Great start! 🎉');
    } else if (filledSlots === maxSlots - 1 && !bundle.dataset.toast2) {
      bundle.dataset.toast2 = 'true';
      showMilestoneToast('Almost there! ⚡');
    }
  }

  // Reset toast flags
  if (filledSlots < 1) delete bundle.dataset.toast1;
  if (filledSlots < maxSlots - 1) delete bundle.dataset.toast2;

  // Animate price (respect reduced motion)
  if (!prefersReducedMotion) {
    const heroPrice = bundle.querySelector('.bundle-pricing-card__hero-price');
    if (heroPrice) {
      heroPrice.classList.remove('price-update');
      void heroPrice.offsetWidth;
      heroPrice.classList.add('price-update');
    }
  }

  // Update social proof counter
  updateSocialProof(bundle);

  console.log('  ✅ Update complete\n');
};

// Advanced social proof system with realistic buying patterns
function updateSocialProof(bundle) {
  const socialProof = bundle.querySelector('[data-bundle-social-proof]');
  if (!socialProof) return;

  // Check if social proof is enabled in admin settings
  const isEnabled = bundle.getAttribute('data-social-proof-enabled') === 'true';
  if (!isEnabled) {
    socialProof.setAttribute('hidden', '');
    return;
  }

  const bundleValueElement = socialProof.querySelector('[data-bundle-counter] .bundle-social-proof__value');
  const trending = socialProof.querySelector('[data-bundle-trending]');
  const filledSlots = bundle.querySelectorAll('[data-product-bundle-variant]:not([available])').length;

  // Get admin-controlled settings with fallback defaults
  const minCount = parseInt(bundle.getAttribute('data-social-proof-min')) || 22;
  let maxCount = parseInt(bundle.getAttribute('data-social-proof-max')) || 55;
  const slowDaysPercent = parseInt(bundle.getAttribute('data-social-proof-slow-days')) || 40;
  const growthRate = parseInt(bundle.getAttribute('data-social-proof-growth')) || 30;

  // Validate settings
  if (maxCount <= minCount) {
    maxCount = minCount + 20;
  }

  // Generate consistent daily seed
  const now = new Date();
  const today = now.toDateString();
  const dayOfWeek = now.getDay();

  // Create deterministic hash from date
  const dayHash = today.split('').reduce((hash, char) => {
    return ((hash << 5) - hash) + char.charCodeAt(0);
  }, 0);

  // Determine if this is a "slow" day based on admin setting
  const slowDayThreshold = slowDaysPercent / 100;
  const dayRandomness = Math.abs(Math.sin(dayHash)) % 1;
  const isSlowDay = dayRandomness < slowDayThreshold;

  // Weekend traffic pattern
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const weekendMultiplier = isWeekend ? 0.7 : 1.0;

  // Calculate base count for the day
  let baseCount;
  if (isSlowDay) {
    const slowMultiplier = 0.4 + (dayRandomness * 0.2);
    baseCount = Math.floor(minCount * slowMultiplier * weekendMultiplier);
  } else {
    const range = maxCount - minCount;
    const dayPosition = Math.abs(Math.sin(dayHash * 2)) % 1;
    baseCount = Math.floor(minCount + (range * dayPosition * weekendMultiplier));
  }

  // Time-of-day progression
  const hour = now.getHours();
  const minute = now.getMinutes();
  const timeProgress = (hour * 60 + minute) / (24 * 60);

  // Realistic hourly curve (peaks mid-day, slow at night)
  const peakHour = 14;
  const hoursFromPeak = Math.abs(hour - peakHour);
  const timeCurve = Math.max(0.3, 1 - (hoursFromPeak / 12) * 0.7);

  // Calculate growth throughout the day
  const growthMultiplier = 1 + (timeProgress * (growthRate / 100));

  // Apply realistic growth with time curve
  const progressiveCount = Math.floor(baseCount * growthMultiplier * timeCurve);

  // Add small random variance (±3% instead of ±5% for stability)
  const variance = Math.floor(progressiveCount * 0.03);
  const minuteHash = minute % 10;
  const variedCount = progressiveCount + (minuteHash > 5 ? variance : -variance);

  // Calculate the raw count
  let calculatedCount = Math.max(Math.floor(minCount * 0.3), variedCount);

  // CRITICAL: Never-decreasing safeguard using localStorage
  const storageKey = `bundle_count_${today}`;
  const storedData = localStorage.getItem(storageKey);

  let displayCount = calculatedCount;

  if (storedData) {
    try {
      const { maxCount: storedMax, lastUpdate } = JSON.parse(storedData);
      // Only use stored value if it's higher than calculated
      if (storedMax > calculatedCount) {
        displayCount = storedMax;
      } else {
        // New high for today - update storage
        displayCount = calculatedCount;
        localStorage.setItem(storageKey, JSON.stringify({
          maxCount: displayCount,
          lastUpdate: now.getTime()
        }));
      }
    } catch (e) {
      // Invalid storage, start fresh
      localStorage.setItem(storageKey, JSON.stringify({
        maxCount: calculatedCount,
        lastUpdate: now.getTime()
      }));
    }
  } else {
    // First time today - store the value
    localStorage.setItem(storageKey, JSON.stringify({
      maxCount: calculatedCount,
      lastUpdate: now.getTime()
    }));
  }

  // Clean up old localStorage entries (keep only today's data)
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('bundle_count_') && key !== storageKey) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    // Silent fail if localStorage is full
  }

  const bundleCount = displayCount;

  // Calculate live visitors count (oscillating between 15-60 based on settings and time)
  // Use a combination of time-based factors and minute-based oscillation
  const visitorBase = Math.floor((minCount + maxCount) / 8); // Roughly 1/8th of bundle count
  const visitorMin = Math.max(3, Math.floor(visitorBase * 0.5)); // At least 3, up to half of base
  const visitorMax = Math.floor(visitorBase * 1.5); // Up to 1.5x base

  // Create oscillation that changes every 30 seconds
  const thirtySecondWindow = Math.floor(now.getTime() / 30000);
  const oscillationSeed = thirtySecondWindow + dayHash;
  const oscillationValue = Math.abs(Math.sin(oscillationSeed * 0.1)) % 1;

  // Apply time-of-day multiplier
  const visitorTimeCurve = Math.max(0.4, 1 - (hoursFromPeak / 14) * 0.6);

  // Calculate current visitors with natural oscillation
  const visitorRange = visitorMax - visitorMin;
  const visitorCount = Math.floor(visitorMin + (visitorRange * oscillationValue * visitorTimeCurve));

  // Update bundle count with smooth animation
  if (bundleValueElement) {
    const currentValue = parseInt(bundleValueElement.textContent) || 0;

    if (currentValue !== bundleCount) {
      bundleValueElement.style.transition = 'opacity 0.15s ease, transform 0.15s ease';
      bundleValueElement.style.transform = 'scale(0.95)';
      bundleValueElement.style.opacity = '0.7';

      setTimeout(() => {
        bundleValueElement.textContent = bundleCount;
        bundleValueElement.style.transform = 'scale(1)';
        bundleValueElement.style.opacity = '1';
      }, 150);
    } else {
      bundleValueElement.textContent = bundleCount;
    }
  }

  // Update live visitors count
  const visitorsElement = socialProof.querySelector('[data-visitors-counter] .bundle-social-proof__value');
  if (visitorsElement) {
    const currentVisitors = parseInt(visitorsElement.textContent) || 0;

    if (currentVisitors !== visitorCount) {
      visitorsElement.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
      visitorsElement.style.transform = 'scale(0.92)';
      visitorsElement.style.opacity = '0.6';

      setTimeout(() => {
        visitorsElement.textContent = visitorCount;
        visitorsElement.style.transform = 'scale(1)';
        visitorsElement.style.opacity = '1';
      }, 200);
    } else {
      visitorsElement.textContent = visitorCount;
    }
  }

  socialProof.removeAttribute('hidden');

  // Show "trending" dropdown on peak days (when filled slots >= 2)
  const isPeakDay = !isSlowDay && baseCount > (minCount + maxCount) / 2;
  if (trending) {
    if (isPeakDay && filledSlots >= 2) {
      trending.removeAttribute('hidden');
      updateTrendingImages(bundle, trending);
    } else {
      trending.setAttribute('hidden', '');
    }
  }
}

// Update trending images with 2 random products from bundle
function updateTrendingImages(bundle, trending) {
  const imagesContainer = trending.querySelector('[data-trending-images]');
  if (!imagesContainer) return;

  // Get all filled product slots with images
  const filledProducts = Array.from(bundle.querySelectorAll('[data-product-bundle-variant]:not([available])')).filter(slot => {
    const img = slot.querySelector('[data-product-bundle-variant-media] img');
    return img && img.src;
  });

  if (filledProducts.length < 2) {
    imagesContainer.innerHTML = '';
    return;
  }

  // Shuffle and pick 2 random products (deterministic based on day for consistency)
  const today = new Date().toDateString();
  const dayHash = today.split('').reduce((hash, char) => {
    return ((hash << 5) - hash) + char.charCodeAt(0);
  }, 0);

  // Use day hash to pick consistent 2 products for the day
  const shuffled = [...filledProducts];
  let seed = Math.abs(dayHash);
  for (let i = shuffled.length - 1; i > 0; i--) {
    seed = (seed * 9301 + 49297) % 233280;
    const j = seed % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const selectedProducts = shuffled.slice(0, 2);

  // Build HTML for 2 product images
  imagesContainer.innerHTML = selectedProducts.map(product => {
    const img = product.querySelector('[data-product-bundle-variant-media] img');
    const src = img.src;
    const alt = img.alt || 'Product';

    return `
      <div class='bundle-social-proof__trending-image'>
        <img src='${src}' alt='${alt}' loading='lazy' />
      </div>
    `;
  }).join('');

  console.log('🔥 Trending images updated with 2 random products');
}

// ================================================================================
// 4. BUNDLE GAMIFICATION - Product bundle progress tracking
// ================================================================================
// Initialize bundle gamification event listeners
(function() {
  // BC_BUNDLE_POLISH: Bryan Counts - Silently check for bundle elements
  // Only log if bundles are actually present on the page
  const testBundle = document.querySelector('product-bundle');
  if (testBundle) {
    console.log('🚀 Initializing bundle gamification system...');
    console.log('✅ Found product-bundle element');
    const testBar = testBundle.querySelector('[data-product-bundle-progress-bar]');
    const testLabel = testBundle.querySelector('[data-bundle-progress-label]');
    const testCount = testBundle.querySelector('[data-bundle-progress-count]');
    console.log('  Progress bar:', testBar ? '✅' : '❌');
    console.log('  Progress label:', testLabel ? '✅' : '❌');
    console.log('  Progress count:', testCount ? '✅' : '❌');
  } else {
    // Silently skip - no bundles on this page
    return;
  }

  // Celebration on add to cart
  document.addEventListener('click', function(e) {
    const button = e.target.closest('[data-product-bundle-submit]');
    if (button && button.classList.contains('bundle-complete') && !button.disabled) {
      console.log('🎉 ADD TO CART CLICKED - Triggering hearts!');
      const rect = button.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      if (window.bundleCelebration) {
        window.bundleCelebration.triggerHeartExplosion(x, y);
      }
    }
  });

  // Direct listeners for bundle events to ensure progress updates
  document.addEventListener('bundle:added', function(e) {
    console.log('📦 bundle:added event fired');
    const bundle = e.target.closest('product-bundle');
    if (bundle && window.updateBundleState) {
      setTimeout(() => window.updateBundleState(bundle), 100);
    }
  });

  document.addEventListener('bundle:removed', function(e) {
    console.log('🗑️ bundle:removed event fired');
    const bundle = e.target.closest('product-bundle');

    // Clear stuck button states when removing items
    if (bundle) {
      const submitButton = bundle.querySelector('[data-product-bundle-submit]');
      if (submitButton) {
        submitButton.removeAttribute('aria-busy');
        submitButton.classList.remove('bundle-complete');
      }

      if (window.updateBundleState) {
        setTimeout(() => window.updateBundleState(bundle), 100);
      }
    }
  });

  // Multiple initialization attempts
  // BC_BUNDLE_POLISH: Bryan Counts - Only log when bundles exist
  function initializeBundles() {
    const bundles = document.querySelectorAll('product-bundle');

    // Only log if bundles are found
    if (bundles.length > 0) {
    console.log(`🔍 Found ${bundles.length} bundle(s)`);

    bundles.forEach((bundle, index) => {
      console.log(`\n📦 Initializing bundle ${index + 1}...`);
      if (window.updateBundleState) {
        window.updateBundleState(bundle);
      }
    });
    }
  }

  // Try immediately if DOM is ready (silently check)
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(initializeBundles, 100);
    setTimeout(initializeBundles, 500); // Backup attempt
    setTimeout(initializeBundles, 1000); // Final attempt
  }

  // Also listen for DOMContentLoaded (silently check)
  document.addEventListener('DOMContentLoaded', function() {
    setTimeout(initializeBundles, 100);
  });

  // Listen for Shopify section loads (silently check)
  document.addEventListener('shopify:section:load', function() {
    setTimeout(initializeBundles, 100);
  });
})();

// Price update animation CSS class helper
const style = document.createElement('style');
style.textContent = `
  .bundle-pricing-card__hero-price.price-update {
    animation: pricePopIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes pricePopIn {
    0% { transform: scale(1); }
    50% { transform: scale(1.08); }
    100% { transform: scale(1); }
  }
`;
document.head.appendChild(style);

// ================================================================================
// 5. SECONDARY MEDIA OVERRIDE - Flickity A11Y for product images
// ================================================================================
// BC_FLICKITY_A11Y & BC_MENU_A11Y: Bryan Counts - IMPORTANT NOTE
// The following accessibility fixes are in theme.js because they must execute
// at specific times during class registration and initialization:
//
// 1. theme.addFlickityDotLabels() - Lines ~42-65 in theme.js
// 2. CarouselElement Flickity labels - Line ~3670 in theme.js
// 3. SecondaryMedia Flickity labels - Line ~3750 in theme.js
// 4. DetailsMega API loading methods - Lines ~3195-3260 in theme.js
//
// These modifications fix critical WAVE accessibility errors and cannot be
// moved to custom files due to JavaScript timing and class registration constraints.
// ================================================================================

// ================================================================================
// CUSTOM: Secondary Media Fade Effect Override
// Changes carousel animation from swipe to fade while preserving all functionality
// Only applies fade effect on desktop (min-width: 768px)
// ================================================================================

(function() {
  // Wait for theme.js to load and define SecondaryMedia
  const initFadeEffect = () => {
    // Store original init method
    const SecondaryMediaProto = customElements.get('secondary-media');
    if (!SecondaryMediaProto) {
      // Not loaded yet, try again
      setTimeout(initFadeEffect, 100);
      return;
    }

    const originalInit = SecondaryMediaProto.prototype.init;

    // Override init method to support fade animation option
    SecondaryMediaProto.prototype.init = function() {
      this.appendChild(this.template.content.cloneNode(true));
      this.mediaCount = this.querySelectorAll('.media').length;

      if (this.mediaCount > 1) {
        // Check if fade effect is requested and if we're on desktop
        const effectType = this.getAttribute('data-effect');
        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        const useFade = effectType === 'fade' && isDesktop;

        // Initialize Flickity - keep all functionality, just change animation
        this.carousel = new Flickity(this, {
          accessibility: false,
          draggable: true, // Keep dragging enabled
          fade: useFade, // Only difference: fade vs slide animation on desktop
          pageDots: true, // Keep dots enabled
          prevNextButtons: false,
          wrapAround: true,
          rightToLeft: theme.config.rtl,
        });

        // BC_FLICKITY_A11Y: Bryan Counts - Add accessible labels for product image carousels with fade effect
        if (typeof theme.addFlickityDotLabels === 'function') {
          theme.addFlickityDotLabels(this.carousel, 'media');
        }

        if (this.mediaCount === 2) {
          this.classList.add('without-dots');
        }

        // CUSTOM: Always show second image first, wait for mouse to stop, then enable navigation
        let mouseNavigationEnabled = false;
        let mouseStopTimer = null;
        const STOP_DETECTION_DELAY = 100; // ms to wait after mouse stops moving
        const POST_STOP_DELAY = 100; // ms pause after stop before enabling navigation

        // Wrap the original onMoveHandler to control when it activates
        const originalOnMove = this.onMoveHandler;
        this.onMoveHandler = (e) => {
          // Clear any pending stop detection
          if (mouseStopTimer) {
            clearTimeout(mouseStopTimer);
          }

          // If navigation not enabled yet, detect when mouse stops
          if (!mouseNavigationEnabled) {
            // Set timer to detect when mouse has stopped moving
            mouseStopTimer = setTimeout(() => {
              // Mouse has stopped, ensure we're on second image
              if (this.carousel.selectedIndex !== 1) {
                this.carousel.select(1);
              }

              // Wait a brief moment after stop, then enable navigation
              setTimeout(() => {
                mouseNavigationEnabled = true;
              }, POST_STOP_DELAY);
            }, STOP_DETECTION_DELAY);
          }

          // Only allow mouse position tracking after stop + pause
          if (mouseNavigationEnabled) {
            originalOnMove.call(this, e);
          }
        };

        this.addEventListener('mouseenter', (e) => {
          // Reset state
          mouseNavigationEnabled = false;
          if (mouseStopTimer) {
            clearTimeout(mouseStopTimer);
          }

          // Always go to second image first
          this.carousel.select(1);
        });

        // Keep mouse move behavior for navigation (now controlled by wrapper)
        this.addEventListener('mousemove', this.onMoveHandler);

        // Reset to first image on leave
        this.addEventListener('mouseleave', (e) => {
          mouseNavigationEnabled = false;
          if (mouseStopTimer) {
            clearTimeout(mouseStopTimer);
          }
          this.onLeaveHandler(e);
        });
      }
    };
  };

  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFadeEffect);
  } else {
    initFadeEffect();
  }
})();

// ================================================================================
// CUSTOM: Collapsed Grid Bottom Corner Rounding
// TODO: Implement bottom corner rounding in future update
// Top corners are handled via CSS and work perfectly
// ================================================================================
// Removed for now - will revisit bottom corner logic later

// ================================================================================
// CUSTOM: Fix Tabs with Sliders - Reinitialize on Tab Change
// ================================================================================
// When switching tabs, sliders need to recalculate dimensions after becoming visible
(function() {
  document.addEventListener('DOMContentLoaded', function() {
    const TabsElementClass = customElements.get('tabs-element');
    if (!TabsElementClass) return;

    const originalTransition = TabsElementClass.prototype.transition;

    TabsElementClass.prototype.transition = async function(fromPanel, toPanel) {
      // Call original transition
      await originalTransition.call(this, fromPanel, toPanel);

      // Wait for layout to complete before initializing sliders
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const sliders = toPanel.querySelectorAll('slider-element');
          sliders.forEach(slider => {
            // If slider hasn't been initialized yet, initialize it
            if (!slider.currentPage) {
              slider.init();
            } else if (slider.reset && slider.update && slider.updateButtons) {
              // If already initialized, reset and force recalculation
              slider._items = null; // Clear cached items
              slider.reset();
              slider.update();
              slider.updateButtons();
            }
          });
        });
      });
    };
  });

  // Also handle API-loaded content (when tab content is loaded dynamically)
  const APIButtonClass = customElements.get('api-button');
  if (APIButtonClass) {
    const originalUpdateSectionRendering = APIButtonClass.prototype.updateSectionRendering;

    APIButtonClass.prototype.updateSectionRendering = function(responseText) {
      // Call original method to update DOM
      originalUpdateSectionRendering.call(this, responseText);

      // Wait for layout to complete before initializing sliders
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const newContent = document.getElementById(this.sectionRenderingId);
          if (newContent) {
            const sliders = newContent.querySelectorAll('slider-element');
            sliders.forEach(slider => {
              if (!slider.currentPage) {
                slider.init();
              }
            });
          }
        });
      });
    };
  }
})();

// ================================================================================
// CUSTOM: Sync Header Section Classes to HTML Tag
// Mirrors header section classes (header-scrolled, header-nav-scrolled, etc.)
// to the <html> tag for global styling control
// ================================================================================
(function() {
  'use strict';

  // Classes to sync from header section to html tag
  // Note: header-sticky is excluded because it causes layout issues when on <html>
  const classesToSync = [
    'header-scrolled',
    'header-nav-scrolled',
    'header-hidden',
    'header-transparent'
  ];

  function syncHeaderClasses() {
    const headerSection = document.querySelector('.header-section');
    if (!headerSection) return;

    // Always remove header-sticky from <html> as it causes layout issues
    document.documentElement.classList.remove('header-sticky');

    // Sync each class
    classesToSync.forEach(className => {
      if (headerSection.classList.contains(className)) {
        document.documentElement.classList.add(className);
      } else {
        document.documentElement.classList.remove(className);
      }
    });
  }

  // Initial sync
  function init() {
    // Wait a bit for header to be initialized
    setTimeout(() => {
      syncHeaderClasses();
    }, 100);
  }

  // Watch for class changes on header section
  function setupObserver() {
    const headerSection = document.querySelector('.header-section');
    if (!headerSection) {
      // Retry if header section not found yet
      setTimeout(setupObserver, 100);
      return;
    }

    // Create MutationObserver to watch for class changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          syncHeaderClasses();
        }
      });
    });

    observer.observe(headerSection, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Initial sync
    syncHeaderClasses();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      setupObserver();
    });
  } else {
    init();
    setupObserver();
  }

  // Also listen for header scroll events as a backup
  document.addEventListener('header:scrolled', () => {
    syncHeaderClasses();
  });

  // Handle Shopify theme editor
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        setupObserver();
      }, 100);
    });
  }
})();

/**
 * ProductCardInfo - Custom element for bundle product cards
 * Extends ProductInfo to handle variant selection in bundle cards
 * Uses view=bundle-card template for AJAX updates
 */
(function() {
  'use strict';

  function defineProductCardInfo() {
    // Get ProductInfo from the custom element registry
    // ProductInfo is registered as 'product-info' custom element
    const ProductInfoElement = customElements.get('product-info');

    if (!ProductInfoElement) {
      console.warn('ProductCardInfo: product-info element not found, retrying...');
      setTimeout(defineProductCardInfo, 100);
      return;
    }

    // ================================================================================
    // 6. PRODUCT CARD INFO - Dynamic product card information
    // ================================================================================
    class ProductCardInfo extends ProductInfoElement {
      constructor() {
        super();
        this._sectionId = 'ProductCard-template';
        this._isUpdating = false; // Flag to prevent infinite loops
        this._lastUpdateTime = 0; // Debounce timestamp
      }

      get pickerType() {
        return this.getAttribute('data-picker-type');
      }

      get productForm() {
        // Override to find bundle form instead of regular product form
        return this.querySelector('form[is="product-bundle-form"]');
      }

      get variantSelectors() {
        // Override to find variant-picker instead of variant-selects (bundle cards use variant-picker)
        return this.querySelector('variant-picker') || this.querySelector('variant-selects');
      }

      connectedCallback() {
        console.log('[ProductCardInfo] connectedCallback', {
          productId: this.productId,
          sectionId: this.sectionId,
          pickerType: this.pickerType,
          hasVariantSelectors: !!this.variantSelectors,
          hasProductForm: !!this.productForm,
          element: this
        });

        // Call parent connectedCallback to set up event listeners
        super.connectedCallback();

        // Set up variant-picker change listener (bundle cards use variant-picker, not variant-selects)
        // Use a MutationObserver to catch when variant-picker is added dynamically
        const setupVariantPickerListener = () => {
          const variantPicker = this.variantSelectors;
          if (variantPicker && !variantPicker.hasAttribute('data-listener-attached')) {
            console.log('[ProductCardInfo] Setting up variant-picker change listener');
            variantPicker.setAttribute('data-listener-attached', 'true');

            // Listen for change events on the variant-picker
            // Handle both SELECT (dropdowns) and INPUT[type="radio"] (swatches/buttons)
            variantPicker.addEventListener('change', (e) => {
              const target = e.target;

              // Only handle select and radio input changes
              const isSelect = target.tagName === 'SELECT';
              const isRadio = target.tagName === 'INPUT' && target.type === 'radio';
              if (!isSelect && !isRadio) return;

              // Prevent infinite loops - don't process if we're already updating
              if (this._isUpdating) {
                console.log('[ProductCardInfo] Ignoring change event - update in progress');
                return;
              }

              // Debounce - don't process if called within 500ms of last update
              const now = Date.now();
              if (now - this._lastUpdateTime < 500) {
                console.log('[ProductCardInfo] Ignoring change event - too soon after last update');
                return;
              }

              console.log('[ProductCardInfo] Variant-picker change event', {
                target: target,
                type: isSelect ? 'SELECT' : 'RADIO',
                value: target.value,
                checked: isRadio ? target.checked : undefined
              });

              // Get all selected option value IDs (like VariantSelects does)
              // Handle both selects and radio buttons
              const selectedOptionValues = [];

              // Get selected values from SELECT elements
              const selects = variantPicker.querySelectorAll('select[is="custom-select"]');
              selects.forEach(select => {
                if (select.selectedOptions.length) {
                  const option = select.selectedOptions[0];
                  const optionValueId = option.getAttribute('data-option-value-id');
                  if (optionValueId) {
                    selectedOptionValues.push(optionValueId);
                  }
                }
              });

              // Get selected values from radio buttons (swatches/buttons)
              const radioInputs = variantPicker.querySelectorAll('input[type="radio"]:checked');
              radioInputs.forEach(radio => {
                const optionValueId = radio.getAttribute('data-option-value-id');
                if (optionValueId) {
                  selectedOptionValues.push(optionValueId);
                }
              });

              console.log('[ProductCardInfo] Selected option value IDs from variant-picker:', selectedOptionValues);

              // Get product URL from the changed element or fallback
              let productUrl = null;
              if (isSelect && target.selectedOptions.length) {
                productUrl = target.selectedOptions[0].getAttribute('data-product-url');
              } else if (isRadio) {
                productUrl = target.getAttribute('data-product-url');
              }
              productUrl = productUrl || this.getAttribute('data-product-url');

              // Manually trigger variant update
              if (productUrl && selectedOptionValues.length > 0) {
                this._isUpdating = true;
                this._lastUpdateTime = now;

                this.renderProductInfo({
                  requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
                  targetId: target.id || target.closest('[data-option-index]')?.id || '',
                  callback: this.handleUpdateProductInfo()
                });
              } else {
                console.warn('[ProductCardInfo] Cannot update - missing productUrl or optionValues', {
                  productUrl,
                  selectedOptionValues
                });
              }
            });
          }
        };

        // Try immediately
        setupVariantPickerListener();

        // Also watch for when variant-picker is added
        const observer = new MutationObserver(() => {
          setupVariantPickerListener();
        });

        observer.observe(this, {
          childList: true,
          subtree: true
        });

        // Store observer for cleanup
        this._variantPickerObserver = observer;
      }

      buildRequestUrlWithParams(url, optionValues) {
        const params = [];
        params.push('view=bundle-card');

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        // Include picker_type if available
        if (this.pickerType) {
          params.push(`picker_type=${this.pickerType}`);
        }

        const requestUrl = `${url}?${params.join('&')}`;
        console.log('[ProductCardInfo] buildRequestUrlWithParams:', {
          url,
          optionValues,
          pickerType: this.pickerType,
          requestUrl
        });
        return requestUrl;
      }

      triggerVariantUpdate() {
        console.log('[ProductCardInfo] triggerVariantUpdate called');
        // Get all selected option value IDs (like VariantSelects does)
        const selectedOptionValues = [];
        const variantPicker = this.variantSelectors;
        if (variantPicker) {
          const selects = variantPicker.querySelectorAll('select[is="custom-select"]');
          selects.forEach(select => {
            if (select.selectedOptions.length) {
              const option = select.selectedOptions[0];
              const optionValueId = option.getAttribute('data-option-value-id');
              if (optionValueId) {
                selectedOptionValues.push(optionValueId);
              }
            }
          });
        }

        console.log('[ProductCardInfo] Selected option value IDs:', selectedOptionValues);

        const productUrl = this.getAttribute('data-product-url');
        if (productUrl && selectedOptionValues.length > 0) {
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
            targetId: variantPicker?.querySelector('select[is="custom-select"]')?.id || '',
            callback: this.handleUpdateProductInfo()
          });
        } else {
          console.warn('[ProductCardInfo] Cannot trigger update - missing productUrl or optionValues', {
            productUrl,
            selectedOptionValues
          });
        }
      }

      updateVariantInputs(variantId) {
        // Override to update bundle form's hidden input
        if (!variantId) return;

        const form = this.productForm;
        if (form) {
          const input = form.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            console.log('[ProductCardInfo] updateVariantInputs: Updated to', variantId);
          }
        } else {
          // Fallback to parent method
          super.updateVariantInputs(variantId);
        }
      }

      disconnectedCallback() {
        // Clean up observer
        if (this._variantPickerObserver) {
          this._variantPickerObserver.disconnect();
        }
        // Call parent cleanup
        super.disconnectedCallback();
      }

      handleOptionValueChange(eventData) {
        console.log('[ProductCardInfo] handleOptionValueChange called', eventData);
        // Call parent method
        super.handleOptionValueChange(eventData);
      }

      updateSourceFromDestination(parsedHTML, id) {
        // Use _sectionId for source (from response) and sectionId for destination (in DOM)
        const sourceId = `${id}-${this._sectionId}-${this.productId}`;
        const destId = `${id}-${this.sectionId}-${this.productId}`;
        const source = parsedHTML.getElementById(sourceId);
        const destination = document.querySelector(`#${destId}`);

        console.log('[ProductCardInfo] updateSourceFromDestination:', {
          id,
          sourceId,
          destId,
          sourceFound: !!source,
          destinationFound: !!destination
        });

        if (source && destination) {
          destination.innerHTML = source.innerHTML;
          destination.removeAttribute('hidden');
        }
      }

      updateOptionValues(parsedHTML) {
        // Include pickerType in variant picker ID lookup
        const variantPickerId = `VariantPicker-${this._sectionId}-${this.productId}${this.pickerType ? `-${this.pickerType}` : ''}`;
        const variantPicker = parsedHTML.getElementById(variantPickerId);

        console.log('[ProductCardInfo] updateOptionValues:', {
          variantPickerId,
          found: !!variantPicker,
          hasVariantSelectors: !!this.variantSelectors
        });

        // Always try to update labels, even if variantPicker is not found
        // We can search for labels directly in the parsed HTML
        if (this.variantSelectors) {
          // Preserve current selections before updating (both selects and radio buttons)
          const currentSelections = {};
          const currentRadioSelections = {};

          // Preserve SELECT selections
          const selects = this.variantSelectors.querySelectorAll('select[is="custom-select"]');
          selects.forEach(select => {
            const optionIndex = select.closest('[data-option-index]')?.getAttribute('data-option-index');
            if (optionIndex && select.value) {
              currentSelections[optionIndex] = select.value;
            }
          });

          // Preserve RADIO button selections
          const radioInputs = this.variantSelectors.querySelectorAll('input[type="radio"]:checked');
          radioInputs.forEach(radio => {
            const optionIndex = radio.closest('[data-option-index]')?.getAttribute('data-option-index');
            if (optionIndex) {
              currentRadioSelections[optionIndex] = radio.value;
            }
          });

          console.log('[ProductCardInfo] Preserving selections:', {
            selects: currentSelections,
            radios: currentRadioSelections
          });

          // Store reference to old variant picker before transition
          const oldVariantPicker = this.variantSelectors;
          let newVariantPicker = null;

          // Create postProcess callback to capture the new node
          const postProcessCallbacks = [
            (newNode) => {
              newVariantPicker = newNode;
              console.log('[ProductCardInfo] viewTransition complete, new node captured:', newNode);
            }
          ];

          // Update labels from parsed HTML - find matching fieldsets and update their labels
          // First, try to find the variant picker in parsed HTML, or search for labels directly
          let parsedVariantPicker = variantPicker;
          if (!parsedVariantPicker) {
            // Fallback: search for variant-picker in parsed HTML by tag name
            const allVariantPickers = parsedHTML.querySelectorAll('variant-picker');
            parsedVariantPicker = Array.from(allVariantPickers).find(picker =>
              picker.id && picker.id.includes(this.productId)
            );
            console.log('[ProductCardInfo] Fallback search for variant picker:', {
              found: !!parsedVariantPicker,
              totalPickers: allVariantPickers.length
            });
          }

          // If we have a variant picker, use viewTransition
          if (variantPicker && parsedVariantPicker) {
            // Update the variant picker (this should update labels too)
            theme.HTMLUpdateUtility.viewTransition(oldVariantPicker, parsedVariantPicker, this.preProcessHtmlCallbacks, postProcessCallbacks);
          }

          // After viewTransition (or immediately if no transition), explicitly update labels from parsed HTML
          // This ensures labels reflect the selected variant values
          setTimeout(() => {
            // Use the new variant picker from postProcess callback, or find it
            if (!newVariantPicker && variantPicker) {
              // Fallback: find the new node (it's inserted before the old one)
              newVariantPicker = oldVariantPicker.previousElementSibling;
              if (!newVariantPicker || !newVariantPicker.matches('variant-picker')) {
                // Another fallback: query by ID and find the visible one
                const variantPickerId = `VariantPicker-${this.sectionId}-${this.productId}${this.pickerType ? `-${this.pickerType}` : ''}`;
                const allPickers = document.querySelectorAll(`variant-picker[id^="${variantPickerId}"]`);
                newVariantPicker = Array.from(allPickers).find(picker => picker.style.display !== 'none');
              }
            }

            // Use new variant picker if found, otherwise fall back to current variantSelectors
            const activeVariantPicker = newVariantPicker || this.variantSelectors;

            // Update labels from parsed HTML - find matching fieldsets and update their labels
            const optionContainers = activeVariantPicker.querySelectorAll('[data-option-index]');
            console.log('[ProductCardInfo] Updating labels - found', optionContainers.length, 'option containers', {
              usingNewPicker: !!newVariantPicker,
              activePicker: activeVariantPicker,
              hasParsedVariantPicker: !!parsedVariantPicker
            });

            optionContainers.forEach(container => {
              const optionIndex = container.getAttribute('data-option-index');

              // Find the label span in current DOM - try multiple selectors
              const currentFormLabel = container.querySelector('.form__label');
              let currentLabelSpan = currentFormLabel?.querySelector('span.font-medium');

              // Fallback: try finding by ID pattern
              if (!currentLabelSpan) {
                const optionPosition = optionIndex.replace('option', '');
                const labelId = `${this.sectionId}-${this.productId}-option${optionPosition}`;
                currentLabelSpan = document.getElementById(labelId);
                console.log('[ProductCardInfo] Looking for label by ID:', labelId, 'found:', !!currentLabelSpan);
              }

              // Find the updated label in parsed HTML - try multiple approaches
              let parsedContainer = null;
              let parsedLabelSpan = null;

              if (parsedVariantPicker) {
                parsedContainer = parsedVariantPicker.querySelector(`[data-option-index="${optionIndex}"]`);
                const parsedFormLabel = parsedContainer?.querySelector('.form__label');
                parsedLabelSpan = parsedFormLabel?.querySelector('span.font-medium');
              }

              // Fallback: try finding by ID in parsed HTML
              if (!parsedLabelSpan) {
                const optionPosition = optionIndex.replace('option', '');
                const labelId = `${this._sectionId}-${this.productId}-option${optionPosition}`;
                parsedLabelSpan = parsedHTML.getElementById(labelId);
                console.log('[ProductCardInfo] Looking for parsed label by ID:', labelId, 'found:', !!parsedLabelSpan);
              }

              // Final fallback: search for product-card-info in parsed HTML and find labels there
              if (!parsedLabelSpan) {
                const parsedProductCardInfo = parsedHTML.querySelector(`product-card-info[data-product-id="${this.productId}"]`);
                if (parsedProductCardInfo) {
                  const parsedContainerInCard = parsedProductCardInfo.querySelector(`[data-option-index="${optionIndex}"]`);
                  if (parsedContainerInCard) {
                    const parsedFormLabelInCard = parsedContainerInCard.querySelector('.form__label');
                    parsedLabelSpan = parsedFormLabelInCard?.querySelector('span.font-medium');
                    console.log('[ProductCardInfo] Found label via product-card-info fallback:', !!parsedLabelSpan);
                  }
                }
              }

              // Update if both exist
              if (currentLabelSpan && parsedLabelSpan) {
                const oldText = currentLabelSpan.textContent.trim();
                const newText = parsedLabelSpan.textContent.trim();
                if (oldText !== newText) {
                  currentLabelSpan.textContent = newText;
                  console.log('[ProductCardInfo] ✅ Updated label for', optionIndex, 'from', oldText, 'to', newText);
                } else {
                  console.log('[ProductCardInfo] Label already correct for', optionIndex, ':', newText);
                }
              } else {
                console.warn('[ProductCardInfo] ❌ Could not update label for', optionIndex, {
                  hasCurrentLabel: !!currentLabelSpan,
                  hasParsedLabel: !!parsedLabelSpan,
                  hasCurrentFormLabel: !!currentFormLabel,
                  hasParsedContainer: !!parsedContainer
                });
              }
            });

            // Restore SELECT selections (after labels are updated) - use active variant picker
            const newSelects = activeVariantPicker.querySelectorAll('select[is="custom-select"]');
            newSelects.forEach(select => {
              const optionIndex = select.closest('[data-option-index]')?.getAttribute('data-option-index');
              if (optionIndex && currentSelections[optionIndex]) {
                // Set value without triggering change event
                select.value = currentSelections[optionIndex];
                // Update the selected attribute for custom-select
                if (select.selectedOptions.length) {
                  select.selectedOptions[0].setAttribute('selected', '');
                }
                console.log('[ProductCardInfo] Restored SELECT selection for', optionIndex, 'to', currentSelections[optionIndex], '(no event)');
              }
            });

            // Restore RADIO button selections (after labels are updated) - use active variant picker
            Object.keys(currentRadioSelections).forEach(optionIndex => {
              const radioGroup = activeVariantPicker.querySelector(`[data-option-index="${optionIndex}"]`);
              if (radioGroup) {
                const radio = radioGroup.querySelector(`input[type="radio"][value="${currentRadioSelections[optionIndex]}"]`);
                if (radio) {
                  radio.checked = true;

                  // Update the label to match the selected radio button value
                  const formLabel = radioGroup.querySelector('.form__label');
                  const labelSpan = formLabel?.querySelector('span.font-medium');
                  if (labelSpan) {
                    labelSpan.textContent = currentRadioSelections[optionIndex];
                    console.log('[ProductCardInfo] Updated label to match selected radio:', optionIndex, '=', currentRadioSelections[optionIndex]);
                  }

                  console.log('[ProductCardInfo] Restored RADIO selection for', optionIndex, 'to', currentRadioSelections[optionIndex], '(no event)');
                }
              }
            });
          }, 50);
        }
      }

      getSelectedVariant(productInfoNode) {
        // Look for product-card-info instead of product-bundle-info
        // productInfoNode is the parsed HTML document
        const selector = `product-card-info[data-product-id="${this.productId}"] [data-selected-variant]`;
        const selectedVariantElement = productInfoNode.querySelector(selector);
        const selectedVariant = selectedVariantElement?.textContent;

        console.log('[ProductCardInfo] getSelectedVariant:', {
          selector,
          found: !!selectedVariantElement,
          hasContent: !!selectedVariant,
          productId: this.productId
        });

        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      handleUpdateProductInfo() {
        return (parsedHTML) => {
          console.log('[ProductCardInfo] handleUpdateProductInfo called');
          const variant = this.getSelectedVariant(parsedHTML);

          this.pickupAvailability?.update(variant);

          // Update option values WITHOUT triggering change events
          // Use a flag to prevent the change listener from firing
          this._isUpdating = true;
          this.updateOptionValues(parsedHTML);

          this.updateURL(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            console.warn('[ProductCardInfo] No variant found, setting unavailable');
            this.setUnavailable();
            this._isUpdating = false; // Reset flag
            return;
          }

          console.log('[ProductCardInfo] Variant found:', variant.id);

          // CRITICAL: Update the [data-selected-variant] script tag in BOTH the form and variant-picker
          // ProductBundleForm.getSelectedVariant() reads from the form
          const form = this.productForm;
          const variantPicker = this.variantSelectors;
          const variantJson = JSON.stringify(variant);

          // Update in variant-picker (where it's rendered)
          if (variantPicker) {
            let variantScript = variantPicker.querySelector('[data-selected-variant]');
            if (variantScript) {
              variantScript.textContent = variantJson;
              console.log('[ProductCardInfo] Updated [data-selected-variant] in variant-picker');
            }
          }

          // Update in form (where ProductBundleForm looks for it)
          if (form) {
            let variantScript = form.querySelector('[data-selected-variant]');
            if (!variantScript) {
              // Create it if it doesn't exist
              variantScript = document.createElement('script');
              variantScript.type = 'application/json';
              variantScript.setAttribute('data-selected-variant', '');
              form.appendChild(variantScript);
            }
            variantScript.textContent = variantJson;
            console.log('[ProductCardInfo] Updated [data-selected-variant] in form');
          }

          // Also update the hidden input [name="id"] in the form
          if (form && variant?.id) {
            const hiddenInput = form.querySelector('input[name="id"]');
            if (hiddenInput) {
              hiddenInput.value = variant.id;
              console.log('[ProductCardInfo] Updated hidden input [name="id"] to:', variant.id);
            }
          }

          // Use overridden updateSourceFromDestination method
          this.updateSourceFromDestination(parsedHTML, 'ProductGallery');
          this.updateSourceFromDestination(parsedHTML, 'Price');
          this.updateSourceFromDestination(parsedHTML, 'BuyButtonPrice');
          this.updateSourceFromDestination(parsedHTML, 'StickyPrice');
          this.updateSourceFromDestination(parsedHTML, 'Sku');
          this.updateSourceFromDestination(parsedHTML, 'Badges');
          this.updateSourceFromDestination(parsedHTML, 'Inventory');
          this.updateSourceFromDestination(parsedHTML, 'Volume');
          this.updateSourceFromDestination(parsedHTML, 'PricePerItem');
          this.updateSourceFromDestination(parsedHTML, 'BackInStock');
          this.updateSourceFromDestination(parsedHTML, 'ProductBundle');


          this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          this.updateSourceFromDestination(parsedHTML, 'QuantityRules');
          this.updateSourceFromDestination(parsedHTML, 'QuantityRulesCart');
          this.updateSourceFromDestination(parsedHTML, 'VolumeNote');

          this.productForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);
          this.productStickyForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              productId: this.productId,
              parsedHTML: parsedHTML,
              variant: variant
            }
          });

          (this.productForm ?? document).dispatchEvent(new CustomEvent('variant:change', {
            detail: {
              variant: variant
            }
          }));

          // Reset update flag after a short delay to allow DOM to settle
          setTimeout(() => {
            this._isUpdating = false;
            console.log('[ProductCardInfo] Update complete, flag reset');
          }, 100);
        };
      }
    }

    // Register the custom element
    if (!customElements.get('product-card-info')) {
      customElements.define('product-card-info', ProductCardInfo);
      console.log('[ProductCardInfo] Custom element registered');
    }
  }

  // Wait for DOM and product-info custom element to be ready
  function initProductCardInfo() {
    if (customElements.get('product-info')) {
      defineProductCardInfo();
    } else {
      // Wait a bit for product-info to be registered
      setTimeout(initProductCardInfo, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCardInfo);
  } else {
    initProductCardInfo();
  }
})();

// ================================================================================
// BUNDLE AUTO DISCOUNT CODE SYSTEM
// Automatically applies discount code when bundle items are added to cart
// ================================================================================
(function() {
  'use strict';

  let isApplyingDiscount = false;

  function getExistingDiscountCodesFromCart(cartJson) {
    const discountCodes = [];
    if (!cartJson) return discountCodes;

    if (cartJson.cart_level_discount_applications) {
      cartJson.cart_level_discount_applications.forEach(application => {
        if (application.type === 'discount_code' && application.title) {
          discountCodes.push(application.title);
        }
      });
    }

    if (cartJson.items) {
      cartJson.items.forEach(item => {
        if (item.line_level_discount_allocations) {
          item.line_level_discount_allocations.forEach(allocation => {
            if (allocation.discount_application && allocation.discount_application.type === 'discount_code' && allocation.discount_application.title) {
              if (!discountCodes.includes(allocation.discount_application.title)) {
                discountCodes.push(allocation.discount_application.title);
              }
            }
          });
        }
      });
    }

    return discountCodes;
  }

  function getExistingDiscountCodesFromDOM(cartDrawer) {
    const discountCodes = [];
    const discountForm = cartDrawer.querySelector('form[is="cart-discount"]');
    if (!discountForm) return discountCodes;

    const discountPills = discountForm.querySelectorAll('button[is="discount-remove"]');
    for (const pill of discountPills) {
      if (pill.hasAttribute('data-discount')) {
        discountCodes.push(pill.getAttribute('data-discount'));
      }
    }
    return discountCodes;
  }

  async function applyAutoDiscountCode(discountCode, bundleElement, cartJson) {
    if (!discountCode || !discountCode.trim()) return false;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return false;

    const sectionId = cartDrawer.getAttribute('data-section-id');
    if (!sectionId) return false;

    let existingDiscounts = [];
    if (cartJson) {
      existingDiscounts = getExistingDiscountCodesFromCart(cartJson);
    }

    if (existingDiscounts.length === 0) {
      await new Promise(resolve => setTimeout(resolve, 300));
      existingDiscounts = getExistingDiscountCodesFromDOM(cartDrawer);
    }

    if (existingDiscounts.includes(discountCode.trim())) {
      return true;
    }

    try {
      const body = JSON.stringify({
        discount: [...existingDiscounts, discountCode.trim()].join(','),
        sections: [sectionId]
      });

      const response = await fetch(theme.routes.cart_update_url, {
        ...theme.utils.fetchConfig('json'),
        body
      });

      const parsedState = await response.json();

      const invalidDiscount = parsedState.discount_codes && parsedState.discount_codes.find(
        discount => discount.code === discountCode.trim() && discount.applicable === false
      );

      if (invalidDiscount) return false;

      const validDiscount = parsedState.discount_codes && parsedState.discount_codes.find(
        discount => discount.code === discountCode.trim() && discount.applicable === true
      );

      if (validDiscount && parsedState.sections && parsedState.sections[sectionId]) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-discount',
          cart: parsedState
        });
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  async function handleBundleCartUpdate(event) {
    if (!event || !event.source) return;
    if (event.source !== 'product-bundle') return;
    if (isApplyingDiscount) return;

    const bundleElements = document.querySelectorAll('product-bundle[data-auto-discount-code]');
    if (bundleElements.length === 0) return;

    isApplyingDiscount = true;

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const cartJson = event.cart;
      for (const bundle of bundleElements) {
        const discountCode = bundle.getAttribute('data-auto-discount-code');
        if (!discountCode || !discountCode.trim()) continue;

        const success = await applyAutoDiscountCode(discountCode.trim(), bundle, cartJson);
        if (success) break;
      }
    } catch (error) {
      // Silently fail
    } finally {
      setTimeout(() => {
        isApplyingDiscount = false;
      }, 1000);
    }
  }

  if (typeof theme !== 'undefined' && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, handleBundleCartUpdate);
  }
})();

/**
 * Dynamic SKU Update for Product Details Custom Section
 *
 * Purpose: Updates SKU display in the specifications accordion when product variants change
 *
 * How it works:
 * 1. Listens for the 'variant:change' custom event dispatched by the theme's variant picker
 * 2. Finds all elements with class 'product__sku' (standard Shopify theme pattern)
 * 3. Updates the SKU text content when a new variant is selected
 *
 * Why: The specifications section renders server-side with the initial variant's SKU,
 * but needs to update dynamically when customers select different variants without page reload.
 * This maintains consistency with other SKU displays throughout the theme.
 *
 * Dependencies:
 * - Requires variant picker to dispatch 'variant:change' event (already implemented in theme.js)
 * - Requires SKU elements to have 'product__sku' class and proper ID format
 *
 * Added: December 2025
 * Modified: N/A
 */
(function() {
  'use strict';

  /**
   * Updates all SKU elements on the page with the new variant's SKU
   * @param {Object} variant - The selected variant object from Shopify
   */
  function updateSKUElements(variant) {
    if (!variant || !variant.sku) {
      console.warn('[SKU Update] No variant or SKU provided');
      return;
    }

    // Find all SKU elements on the page (there may be multiple)
    const skuElements = document.querySelectorAll('.product__sku');

    if (skuElements.length === 0) {
      console.warn('[SKU Update] No SKU elements found on page');
      return;
    }

    // Update each SKU element with the new variant's SKU
    skuElements.forEach(element => {
      if (element.textContent !== variant.sku) {
        element.textContent = variant.sku;
        console.log('[SKU Update] Updated SKU to:', variant.sku);
      }
    });
  }

  /**
   * Initialize SKU update listeners
   * Sets up event listener for variant changes
   */
  function initSKUUpdater() {
    // Listen for the variant:change custom event dispatched by theme.js
    document.addEventListener('variant:change', function(event) {
      console.log('[SKU Update] variant:change event received', event);
      console.log('[SKU Update] event.detail:', event.detail);

      if (event.detail && event.detail.variant) {
        console.log('[SKU Update] Variant object:', event.detail.variant);
        updateSKUElements(event.detail.variant);
      } else {
        console.warn('[SKU Update] No variant in event.detail');
      }
    });

    console.log('[SKU Update] Listener initialized - waiting for variant:change events...');

    // Also listen to the pubsub event as a backup
    if (typeof theme !== 'undefined' && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, function(event) {
        console.log('[SKU Update] variantChange pubsub event received', event);
        if (event && event.data && event.data.variant) {
          console.log('[SKU Update] Variant from pubsub:', event.data.variant);
          updateSKUElements(event.data.variant);
        }
      });
      console.log('[SKU Update] Also listening to pubsub variantChange events');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSKUUpdater);
  } else {
    initSKUUpdater();
  }
})();
